import axios from 'axios';
import type { ApiResponse, AuthLoginResponse, AuthMeResponse } from '@/types/auth';
import type { BrandAdsReportPayload } from '@/utils/brandAdsReport';
import type { DailySpendPayload } from '@/types/dailySpend';
import type { NWordPage, NWordSortField } from '@/types/nWords';
import type {
  ProfileDailyExcelConfig,
  ProfileDailyExcelPreview,
  ReportCampaignList,
  ReportCampaignMatch,
  ReportCampaignKey,
  ReportSegment,
} from '@/types/profileDailyExcel';
import type {
  AmcAudience,
  AmcAudiencesPayload,
  AmcContextPayload,
  AmcSyncPayload,
  CreateAmcAudienceBody,
  CreateAmcLookalikeBody,
  UpdateAmcAudienceBody,
} from '@/types/amcAudiences';
import type { CampaignsApiResponse, CampaignBudgetUpdate } from '@/types/campaigns';
import type { CampaignSettingsPatch } from '@/api/campaignSettingsUpdate';
import type { BudgetUsageListResponse, BudgetUsageQueryParams } from '@/types/budgetUsage';
import type { PortfoliosListResponse } from '@/types/portfolios';
import type { SpKeywordApplyAction } from '@/types/keywordRecommendations';
import type { BudgetRuleApiProduct, CampaignAdProduct } from '@/types/spCampaigns';
import type {
  HourlyBudgetGroupPayload,
  HourlyBudgetListData,
  HourlyBudgetListItem,
  HourlyBudgetListParams,
  MissingBaseBudgetListData,
  SetBaseBudgetPayload,
} from '@/types/hourlyBudget';
import type { SmartBudgetPlan } from '@/types/smartBudget';
import type { FlagPreviewData, FlagPreviewRequest } from '@/types/automationFlags';
import type { WorkspaceKeywordBidJobCreateBody } from '@/api/workspaceKeywordBidTypes';
import type { WorkspaceSearchTermNegateJobCreateBody } from '@/api/workspaceSearchTermNegateTypes';
import type { WorkspaceCampaignPlacementJobCreateBody } from '@/api/workspaceCampaignPlacementTypes';
import { clearStoredSession, getStoredSession, setStoredSession, type StoredAuthSession } from '@/api/authStorage';
import { normalizeAuthUser } from '@/api/authUser';
import type { MetricFilterPayload } from '@/utils/workspaceMetricFilters';
import type {
  AutoBiddingPreview,
  AutoBiddingRunLog,
  AutoBiddingSavePayload,
  AutoBiddingSettings,
} from '@/utils/autoBidding';
import type {
  OutOfBudgetBoostSavePayload,
  OutOfBudgetBoostSettings,
} from '@/utils/outOfBudgetBoost';
import type { ReportAnalysisConfig, ReportAnalysisInventory, ReportAnalysisJob } from '@/types/reportAnalysis';
import type {
  SovKeywordAddResult,
  SovKeywordDetail,
  SovKeywordList,
  SovSummary,
  SovImportCandidate,
} from '@/types/shareOfVoice';

/** Resolve API origin. Production never uses plain `http://` (mixed content on HTTPS hosts). */
function resolveApiBaseUrl(): string {
  const configured = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim() ?? '';
  if (import.meta.env.DEV) {
    return configured || 'http://localhost:8000';
  }
  // Netlify (HTTPS): empty = same-origin → proxied via public/_redirects
  if (!configured || configured.startsWith('http://')) {
    return '';
  }
  return configured.replace(/\/$/, '');
}

const API_BASE_URL = resolveApiBaseUrl();

/** Calls that must not attach a stale Bearer token (login, session probe). */
const publicApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

/** Heavy list/report endpoints against remote RDS need longer than the default 30s. */
const HEAVY_REQUEST_TIMEOUT_MS = 60_000;

/** Extract FastAPI `detail.error` from an axios error response. */
export function getApiErrorCode(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const detail = error.response?.data?.detail;
  if (detail && typeof detail === 'object' && detail !== null && 'error' in detail) {
    const code = (detail as { error?: unknown }).error;
    return typeof code === 'string' ? code : null;
  }
  return null;
}

export function isAmazonReauthRequiredError(error: unknown): boolean {
  return getApiErrorCode(error) === 'amazon_lwa_reauth_required';
}

api.interceptors.request.use((config) => {
  const token = getStoredSession()?.accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isAmazonReauthRequiredError(error) && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('amazon-lwa-reauth-required', { detail: error }));
    }
    return Promise.reject(error);
  }
);

function sessionFromLoginResponse(data: AuthLoginResponse): StoredAuthSession {
  const session: StoredAuthSession = {
    accessToken: data.access_token,
    refreshToken: data.refresh_token ?? null,
    user: normalizeAuthUser(data.user as Record<string, unknown>),
  };
  setStoredSession(session);
  return session;
}

export async function authLogin(email: string, password: string): Promise<StoredAuthSession> {
  const { data } = await publicApi.post<AuthLoginResponse>('/api/v1/auth/login', { email, password });
  return sessionFromLoginResponse(data);
}

export async function authSignup(email: string, password: string): Promise<StoredAuthSession> {
  const { data } = await publicApi.post<AuthLoginResponse>('/api/v1/auth/signup', { email, password });
  return sessionFromLoginResponse(data);
}

export async function authGoogleAuthorizeUrl(): Promise<string> {
  const { data } = await publicApi.get<{ authorize_url: string }>('/api/v1/auth/google/authorize');
  return data.authorize_url;
}

export async function authGoogleExchange(code: string): Promise<StoredAuthSession> {
  const { data } = await publicApi.post<AuthLoginResponse>('/api/v1/auth/google/exchange', { code });
  return sessionFromLoginResponse(data);
}

export async function authFetchMe(accessToken: string): Promise<AuthMeResponse> {
  const { data } = await publicApi.get<AuthMeResponse>('/api/v1/auth/me', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return data;
}

export async function authCompleteProfile(
  accessToken: string,
  body: { firstName: string; lastName: string; companyName: string }
): Promise<AuthMeResponse> {
  const { data } = await publicApi.post<AuthMeResponse>(
    '/api/v1/auth/profile',
    {
      first_name: body.firstName.trim(),
      last_name: body.lastName.trim(),
      company_name: body.companyName.trim(),
    },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  return data;
}

export function authClearClientSession(): void {
  clearStoredSession();
}

export async function authChangePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await api.post('/api/v1/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
}

/** Optional dashboard schedule overlap filter (ISO dates, both required when used). */
export type CampaignScheduleFilterParams = {
  scheduleStartDate: string;
  scheduleEndDate: string;
};

/** Optional server-side list filters for unified campaigns endpoint. */
export type ProfileCampaignListFilters = {
  adProduct?: 'ALL' | 'SP' | 'SB' | 'SD';
  status?: 'ALL' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  search?: string;
  portfolioId?: 'ALL' | 'NONE' | string;
};

function scheduleFilterQueryParams(
  schedule?: CampaignScheduleFilterParams | null
): Record<string, string> | undefined {
  if (!schedule?.scheduleStartDate || !schedule?.scheduleEndDate) return undefined;
  return {
    scheduleStartDate: schedule.scheduleStartDate,
    scheduleEndDate: schedule.scheduleEndDate,
  };
}

function profileCampaignListQueryParams(
  schedule?: CampaignScheduleFilterParams | null,
  listFilters?: ProfileCampaignListFilters | null
): Record<string, string> | undefined {
  const params: Record<string, string> = {
    ...(scheduleFilterQueryParams(schedule) ?? {}),
  };
  if (listFilters?.adProduct && listFilters.adProduct !== 'ALL') {
    params.adProduct = listFilters.adProduct;
  }
  if (listFilters?.status && listFilters.status !== 'ALL') {
    params.status = listFilters.status;
  }
  const search = listFilters?.search?.trim();
  if (search) {
    params.search = search;
  }
  if (listFilters?.portfolioId && listFilters.portfolioId !== 'ALL') {
    params.portfolioId = listFilters.portfolioId;
  }
  return Object.keys(params).length > 0 ? params : undefined;
}

export type StreamV2KeywordOption = {
  keywordId: string;
  keywordText: string;
  matchType: string;
  campaignId: string;
  adProduct: string;
};

export type StreamAnalyticsQueryInput = {
  startTime?: string;
  endTime?: string;
  timezone?: string;
  days?: number;
  view?: string;
  limit?: number;
  campaignIds?: string[];
  portfolioIds?: string[];
  keywordIds?: string[];
  searchTerms?: string[];
  asins?: string[];
  q?: string;
};

export function streamAnalyticsQueryParams(
  params?: StreamAnalyticsQueryInput | null
): Record<string, string | number | string[]> | undefined {
  if (!params) return undefined;
  const next: Record<string, string | number | string[]> = {};
  if (params.startTime) next.startTime = params.startTime;
  if (params.endTime) next.endTime = params.endTime;
  if (params.timezone) next.timezone = params.timezone;
  if (params.days !== undefined) next.days = params.days;
  if (params.view) next.view = params.view;
  if (params.limit !== undefined) next.limit = params.limit;
  const q = params.q?.trim();
  if (q) next.q = q;
  const campaignIds = params.campaignIds?.map((id) => id.trim()).filter(Boolean);
  if (campaignIds?.length) {
    next.campaignIds = [...new Set(campaignIds)].sort().join(',');
  }
  const portfolioIds = params.portfolioIds?.map((id) => id.trim()).filter(Boolean);
  if (portfolioIds?.length) {
    next.portfolioIds = [...new Set(portfolioIds)].sort().join(',');
  }
  const keywordIds = params.keywordIds?.map((id) => id.trim()).filter(Boolean);
  if (keywordIds?.length) {
    next.keywordIds = [...new Set(keywordIds)].sort().join(',');
  }
  const searchTerms = params.searchTerms?.map((term) => term.trim()).filter(Boolean);
  if (searchTerms?.length) {
    next.searchTerms = [...new Set(searchTerms)].sort();
  }
  const asins = params.asins?.map((asin) => asin.trim()).filter(Boolean);
  if (asins?.length) {
    next.asins = [...new Set(asins)].sort().join(',');
  }
  return Object.keys(next).length > 0 ? next : undefined;
}

/** Repeat array params (`searchTerms=a&searchTerms=b`) so commas inside a term stay intact. */
export function serializeStreamQueryParams(
  params: Record<string, string | number | string[]>
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, item);
    } else {
      search.append(key, String(value));
    }
  }
  return search.toString();
}

function extractCampaignRecords(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) {
    return data.filter((x): x is Record<string, unknown> => x !== null && typeof x === 'object');
  }
  if (!data || typeof data !== 'object') return [];
  const d = data as Record<string, unknown>;
  const rawList = (d.campaigns as unknown[]) || (d.items as unknown[]) || [];
  if (!Array.isArray(rawList)) return [];
  return rawList.filter((x): x is Record<string, unknown> => x !== null && typeof x === 'object');
}

export const adsApi = {
  getManagerAccounts: (): Promise<ApiResponse<unknown>> =>
    api.get('/api/v1/ads/manager-accounts').then((res) => res.data),

  getAdvertisingProfiles: (): Promise<ApiResponse<unknown>> =>
    api.get('/api/v1/ads/advertising-profiles').then((res) => res.data),

  getProfileSyncStatus: (
    profileId: string
  ): Promise<
    ApiResponse<{
      lastSyncedAt: string | null;
      status: string;
      lastError: string | null;
      nextScheduledAt: string;
    }>
  > =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sync-status`)
      .then((res) => res.data),

  getRetailKpis: (
    profileId: string,
    params: {
      startDate: string;
      endDate: string;
      compareStartDate?: string;
      compareEndDate?: string;
    }
  ): Promise<ApiResponse<WorkspaceRetailKpisResponse>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/retail-kpis`, {
        params,
      })
      .then((res) => res.data),

  triggerProfileCatalogSync: (
    profileId: string
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/entity-catalog/sync`)
      .then((res) => res.data),

  syncCampaignCatalog: (
    profileId: string,
    campaignId: string,
    product: CampaignAdProduct
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/campaigns/${encodeURIComponent(campaignId)}/sync`,
        {},
        {
          params: { product },
          timeout: HEAVY_REQUEST_TIMEOUT_MS,
        }
      )
      .then((res) => res.data),

  getSpCampaigns: (
    profileId: string,
    schedule?: CampaignScheduleFilterParams | null
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/campaigns`, {
        params: scheduleFilterQueryParams(schedule),
      })
      .then((res) => res.data),

  /**
   * Unified campaigns list: single DB-backed `/ads/profiles/.../campaigns` endpoint.
   */
  getProfileCampaigns: (
    profileId: string,
    schedule?: CampaignScheduleFilterParams | null,
    listFilters?: ProfileCampaignListFilters | null
  ): Promise<ApiResponse<{ campaigns: Record<string, unknown>[] }>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/campaigns`, {
        params: profileCampaignListQueryParams(schedule, listFilters),
      })
      .then((res) => res.data),

  getProfileCampaign: (
    profileId: string,
    campaignId: string,
    adProduct?: CampaignAdProduct
  ): Promise<ApiResponse<{ campaign: Record<string, unknown> }>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/campaigns/${encodeURIComponent(campaignId)}`,
        { params: adProduct ? { adProduct } : undefined }
      )
      .then((res) => res.data),

  fetchProfileCampaigns: async (
    profileId: string,
    schedule?: CampaignScheduleFilterParams | null,
    listFilters?: ProfileCampaignListFilters | null
  ): Promise<ApiResponse<{ campaigns: Record<string, unknown>[] }>> =>
    adsApi.getProfileCampaigns(profileId, schedule, listFilters),

  fetchProfileCampaignsSequential: async (
    profileId: string,
    schedule?: CampaignScheduleFilterParams | null,
    listFilters?: ProfileCampaignListFilters | null
  ): Promise<
    | { ok: true; campaigns: Record<string, unknown>[] }
    | { ok: false; errorResponse: ApiResponse<Record<string, unknown>> }
  > => {
    const res = await adsApi.getProfileCampaigns(profileId, schedule, listFilters);
    if (!res.success) {
      return { ok: false, errorResponse: res as ApiResponse<Record<string, unknown>> };
    }
    return { ok: true, campaigns: extractCampaignRecords(res.data) };
  },

  listSpAdGroups: (
    profileId: string,
    body: {
      include_extended_data_fields?: boolean;
      states?: Array<'ENABLED' | 'PAUSED' | 'ARCHIVED'>;
      max_results?: number;
      next_token?: string | null;
      campaign_ids?: string[];
      ad_group_ids?: string[];
    },
    schedule?: CampaignScheduleFilterParams | null
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/ad-groups/list`, body, {
        params: scheduleFilterQueryParams(schedule),
        timeout: HEAVY_REQUEST_TIMEOUT_MS,
      })
      .then((res) => res.data),

  listSpProductAds: (
    profileId: string,
    body: {
      include_extended_data_fields?: boolean;
      states?: Array<'ENABLED' | 'PAUSED' | 'ARCHIVED'>;
      max_results?: number;
      next_token?: string | null;
      campaign_ids?: string[];
      ad_group_ids?: string[];
      ad_ids?: string[];
    },
    schedule?: CampaignScheduleFilterParams | null
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/product-ads/list`, body, {
        params: scheduleFilterQueryParams(schedule),
      })
      .then((res) => res.data),

  getWorkspaceAdProductsPage: (
    profileId: string,
    body: {
      limit: number;
      offset: number;
      campaignIds?: string[];
      adGroupIds?: string[];
      appliedFilter?: Record<string, unknown>;
      state?: string;
      search?: string;
      sortBy?: string;
      sortDir?: 'asc' | 'desc';
      startDate?: string;
      endDate?: string;
      metricFilters?: MetricFilterPayload[];
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/workspace/ad-products`, body)
      .then((res) => res.data),

  getWorkspaceAdvertisedProductsPage: (
    profileId: string,
    body: {
      limit: number;
      offset: number;
      campaignIds?: string[];
      adGroupIds?: string[];
      appliedFilter?: Record<string, unknown>;
      state?: string;
      search?: string;
      sortBy?: string;
      sortDir?: 'asc' | 'desc';
      startDate?: string;
      endDate?: string;
      metricFilters?: MetricFilterPayload[];
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/workspace/advertised-products`,
        body
      )
      .then((res) => res.data),

  getWorkspaceEntityPage: (
    profileId: string,
    entity: 'portfolios' | 'campaigns' | 'ad-groups' | 'keywords',
    body: {
      limit: number;
      offset: number;
      startDate: string;
      endDate: string;
      columnFilters?: {
        search?: string;
        status?: string;
        adProduct?: string;
        matchType?: string;
        campaignStatus?: string;
      };
      appliedFilter?: Record<string, unknown>;
      sortBy?: string;
      sortDir?: 'asc' | 'desc';
      metricFilters?: MetricFilterPayload[];
      /** When false, list every catalog campaign even if it has no report rows. */
      requireActivity?: boolean;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/workspace/${entity}`, body)
      .then((res) => res.data),

  getWorkspaceSearchTermsPage: (
    profileId: string,
    body: {
      limit: number;
      offset: number;
      startDate: string;
      endDate: string;
      columnFilters?: {
        search?: string;
        matchType?: string;
        negated?: string;
      };
      appliedFilter?: Record<string, unknown>;
      campaignIds?: string[];
      metricFilters?: MetricFilterPayload[];
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/workspace/search-terms`, body, {
        timeout: HEAVY_REQUEST_TIMEOUT_MS,
      })
      .then((res) => res.data),

  getSearchTermNWords: (
    profileId: string,
    params: {
      startDate: string;
      endDate: string;
      q?: string;
      wordCounts?: number[];
      sortBy?: NWordSortField;
      sortDir?: 'asc' | 'desc';
      page?: number;
      pageSize?: number;
    }
  ): Promise<ApiResponse<NWordPage>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/search-terms/n-words`, {
        params: {
          startDate: params.startDate,
          endDate: params.endDate,
          ...(params.q ? { q: params.q } : {}),
          ...(params.wordCounts?.length ? { wordCounts: params.wordCounts.join(',') } : {}),
          sortBy: params.sortBy,
          sortDir: params.sortDir,
          page: params.page,
          pageSize: params.pageSize,
        },
        timeout: HEAVY_REQUEST_TIMEOUT_MS,
      })
      .then((res) => res.data),

  getSbKeywords: (
    profileId: string,
    params?: { start_index?: number; count?: number; campaignId?: string },
    schedule?: CampaignScheduleFilterParams | null
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/keywords`, {
        params: {
          start_index: params?.start_index ?? 0,
          count: params?.count ?? 500,
          ...(params?.campaignId ? { campaignId: params.campaignId } : {}),
          ...scheduleFilterQueryParams(schedule),
        },
      })
      .then((res) => res.data),

  listSbTargets: (
    profileId: string,
    body?: Record<string, unknown>,
    schedule?: CampaignScheduleFilterParams | null
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/targets/list`, body ?? {}, {
        params: scheduleFilterQueryParams(schedule),
      })
      .then((res) => res.data),

  getSdAdGroups: (
    profileId: string,
    schedule?: CampaignScheduleFilterParams | null
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sd/ad-groups`, {
        params: scheduleFilterQueryParams(schedule),
      })
      .then((res) => res.data),

  getSdTargets: (
    profileId: string,
    schedule?: CampaignScheduleFilterParams | null
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sd/targets`, {
        params: scheduleFilterQueryParams(schedule),
      })
      .then((res) => res.data),

  getSdProductAds: (
    profileId: string,
    schedule?: CampaignScheduleFilterParams | null,
    params?: { campaignIds?: string[] }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sd/product-ads`, {
        params: {
          ...scheduleFilterQueryParams(schedule),
          ...(params?.campaignIds?.length ? { campaignIds: params.campaignIds.join(',') } : {}),
        },
      })
      .then((res) => res.data),

  getSdCreatives: (
    profileId: string,
    params?: { campaignIds?: string[]; adGroupIds?: string[] }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sd/creatives`, {
        params: {
          ...(params?.campaignIds?.length ? { campaignIds: params.campaignIds.join(',') } : {}),
          ...(params?.adGroupIds?.length ? { adGroupIds: params.adGroupIds.join(',') } : {}),
        },
      })
      .then((res) => res.data),

  listSbAdGroups: (
    profileId: string,
    body: {
      include_extended_data_fields?: boolean;
      campaign_ids?: string[];
      ad_group_ids?: string[];
      states?: string[];
      name_match_type?: 'EXACT_MATCH' | 'BROAD_MATCH' | 'PHRASE_MATCH' | 'CONTAINS';
      names?: string[];
      max_results?: number;
    },
    schedule?: CampaignScheduleFilterParams | null
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/ad-groups/list`, body, {
        params: scheduleFilterQueryParams(schedule),
        timeout: HEAVY_REQUEST_TIMEOUT_MS,
      })
      .then((res) => res.data),

  listSpKeywords: (
    profileId: string,
    body: {
      include_extended_data_fields?: boolean;
      states?: Array<'ENABLED' | 'PAUSED' | 'ARCHIVED'>;
      campaign_ids?: string[];
      ad_group_ids?: string[];
      keyword_ids?: string[];
      match_types?: Array<'EXACT' | 'PHRASE' | 'BROAD'>;
      max_results?: number;
      next_token?: string | null;
    },
    schedule?: CampaignScheduleFilterParams | null
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/keywords/list`, body, {
        params: scheduleFilterQueryParams(schedule),
        timeout: HEAVY_REQUEST_TIMEOUT_MS,
      })
      .then((res) => res.data),

  listSpTargets: (
    profileId: string,
    body: {
      states?: Array<'ENABLED' | 'PAUSED' | 'ARCHIVED'>;
      campaign_ids?: string[];
      ad_group_ids?: string[];
      max_results?: number;
      next_token?: string | null;
    },
    schedule?: CampaignScheduleFilterParams | null
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/targets/list`, body, {
        params: scheduleFilterQueryParams(schedule),
      })
      .then((res) => res.data),

  listSpNegativeKeywords: (
    profileId: string,
    body: {
      campaign_ids?: string[];
      ad_group_ids?: string[];
      negative_keyword_ids?: string[];
      states?: Array<'ENABLED' | 'PAUSED'>;
      match_types?: Array<'NEGATIVE_EXACT' | 'NEGATIVE_PHRASE'>;
      max_results?: number;
      next_token?: string | null;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/negative-keywords/list`,
        body
      )
      .then((res) => res.data),

  createSpNegativeKeywords: (
    profileId: string,
    body: {
      negative_keywords: Array<{
        campaign_id: string;
        ad_group_id: string;
        keyword_text: string;
        match_type: 'NEGATIVE_EXACT' | 'NEGATIVE_PHRASE';
        state?: 'ENABLED' | 'PAUSED';
      }>;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/negative-keywords`, body)
      .then((res) => res.data),

  createSpCampaignNegativeKeywords: (
    profileId: string,
    body: {
      negative_keywords: Array<{
        campaign_id: string;
        keyword_text: string;
        match_type: 'NEGATIVE_EXACT' | 'NEGATIVE_PHRASE';
        state?: 'ENABLED' | 'PAUSED';
      }>;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/campaign-negative-keywords`,
        body
      )
      .then((res) => res.data),

  updateSpNegativeKeywords: (
    profileId: string,
    body: {
      negative_keywords: Array<{
        negative_keyword_id: string;
        state: 'ENABLED' | 'PAUSED';
      }>;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .put(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/negative-keywords`, body)
      .then((res) => res.data),

  deleteSpNegativeKeywords: (
    profileId: string,
    body: { negative_keyword_ids: string[] }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/negative-keywords/delete`,
        body
      )
      .then((res) => res.data),

  listSbNegativeKeywords: (
    profileId: string,
    params?: {
      campaignId?: string;
      adGroupId?: string;
      negativeKeywordId?: string;
      stateFilter?: string;
      matchTypeFilter?: string;
      keywordText?: string;
      startIndex?: number;
      count?: number;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/negative-keywords`, {
        params: {
          startIndex: params?.startIndex ?? 0,
          count: params?.count ?? 500,
          ...(params?.campaignId ? { campaignId: params.campaignId } : {}),
          ...(params?.adGroupId ? { adGroupId: params.adGroupId } : {}),
          ...(params?.negativeKeywordId ? { negativeKeywordId: params.negativeKeywordId } : {}),
          ...(params?.stateFilter ? { stateFilter: params.stateFilter } : {}),
          ...(params?.matchTypeFilter ? { matchTypeFilter: params.matchTypeFilter } : {}),
          ...(params?.keywordText ? { keywordText: params.keywordText } : {}),
        },
      })
      .then((res) => res.data),

  createSbNegativeTargets: (
    profileId: string,
    body: {
      negative_targets: Array<{
        campaign_id: string;
        ad_group_id: string;
        asin?: string;
        brand?: string;
      }>;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/negative-targets`, body)
      .then((res) => res.data),

  getSbKeywordBidRecommendations: (
    profileId: string,
    body: {
      campaignId?: string;
      adFormat?: 'productCollection';
      keywords?: Array<{ keywordText: string; matchType: 'broad' | 'phrase' | 'exact' }>;
      themes?: Array<{
        themeType: 'KEYWORDS_RELATED_TO_YOUR_BRAND' | 'KEYWORDS_RELATED_TO_YOUR_LANDING_PAGES';
      }>;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/recommendations/bids`, body, {
        timeout: 20000,
      })
      .then((res) => res.data),

  getSbKeywordRecommendations: (
    profileId: string,
    body: {
      asins?: string[];
      url?: string;
      campaignId?: string;
      maxNumSuggestions?: number;
      goal?: 'PAGE_VISIT' | 'BRAND_IMPRESSION_SHARE' | 'ACQUIRE_NEW_CUSTOMERS' | 'AD_VIEWS';
      creativeType?: 'PRODUCT_COLLECTION';
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/keywords/recommendations`,
        body,
        { timeout: 20000 }
      )
      .then((res) => res.data),

  getSbCategoryRecommendations: (
    profileId: string,
    body: { asins: string[]; campaignId?: string }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/recommendations/targets/category`,
        body,
        { timeout: 20000 }
      )
      .then((res) => res.data),

  getSbProductRecommendations: (
    profileId: string,
    body: { asins: string[]; maxResults?: number }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/recommendations/targets/product/list`,
        body,
        { timeout: 20000 }
      )
      .then((res) => res.data),

  createSbNegativeKeywords: (
    profileId: string,
    body: {
      negative_keywords: Array<{
        campaign_id: string;
        ad_group_id: string;
        keyword_text: string;
        match_type: 'negativeExact' | 'negativePhrase';
      }>;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/negative-keywords`, body)
      .then((res) => res.data),

  updateSbNegativeKeywords: (
    profileId: string,
    body: {
      negative_keywords: Array<{
        negative_keyword_id: string;
        campaign_id: string;
        ad_group_id: string;
        state: 'enabled' | 'archived';
      }>;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .put(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/negative-keywords`, body)
      .then((res) => res.data),

  deleteSbNegativeKeyword: (
    profileId: string,
    negativeKeywordId: string
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .delete(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/negative-keywords/${encodeURIComponent(negativeKeywordId)}`
      )
      .then((res) => res.data),

  createSpCampaigns: (
    profileId: string,
    body: { campaigns: Array<Record<string, unknown>> }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/campaigns`, body)
      .then((res) => res.data),

  createSbCampaigns: (
    profileId: string,
    body: { campaigns: Array<Record<string, unknown>> }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/campaigns`, body)
      .then((res) => res.data),

  createSdCampaigns: (
    profileId: string,
    body: { campaigns: Array<Record<string, unknown>> }
  ): Promise<ApiResponse<unknown>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sd/campaigns`, body)
      .then((res) => res.data),

  createSpAdGroups: (
    profileId: string,
    body: { adGroups: Array<Record<string, unknown>> }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/ad-groups`, body)
      .then((res) => res.data),

  getProductMetadata: (
    profileId: string,
    body: Record<string, unknown>
  ): Promise<ApiResponse<Record<string, unknown>>> => {
    const asins = Array.isArray(body.asins) ? body.asins : [];
    const skus = Array.isArray(body.skus) ? body.skus : [];
    const requested = typeof body.pageSize === 'number' && body.pageSize > 0 ? body.pageSize : null;
    const pageSize = requested ?? Math.min(100, Math.max(25, asins.length, skus.length));
    const pageIndex = typeof body.pageIndex === 'number' ? body.pageIndex : 0;
    return api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/product/metadata`,
        {
          checkItemDetails: true,
          checkEligibility: true,
          ...body,
          pageSize,
          pageIndex,
        },
        { timeout: 20000 }
      )
      .then((res) => res.data);
  },

  getProductEligibility: (
    profileId: string,
    body: Record<string, unknown>
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/product/eligibility`, body)
      .then((res) => res.data),

  getSpKeywordRecommendations: (
    profileId: string,
    body: Record<string, unknown>
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/keywords/recommendations`,
        body
      )
      .then((res) => res.data),

  getSpProductRecommendations: (
    profileId: string,
    body: Record<string, unknown>
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/products/recommendations`,
        body
      )
      .then((res) => res.data),

  getSpCategoryRecommendations: (
    profileId: string,
    body: Record<string, unknown>
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/categories/recommendations`,
        body
      )
      .then((res) => res.data),

  getSpTargetBidRecommendations: (
    profileId: string,
    body: Record<string, unknown>
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/targets/bid-recommendations`,
        body
      )
      .then((res) => res.data),

  createSpProductAds: (
    profileId: string,
    body: { productAds: Array<Record<string, unknown>> }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/product-ads`, body)
      .then((res) => res.data),

  createSpKeywords: (
    profileId: string,
    body: { keywords: Array<Record<string, unknown>> }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/keywords`, body)
      .then((res) => res.data),

  createSpTargets: (
    profileId: string,
    body: { targetingClauses: Array<Record<string, unknown>> }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/targets`, body)
      .then((res) => res.data),

  createSpNegativeTargets: (
    profileId: string,
    body: { negativeTargetingClauses: Array<Record<string, unknown>> }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/negative-targets`, body)
      .then((res) => res.data),

  createSpCampaignNegativeTargets: (
    profileId: string,
    body: { negativeTargetingClauses: Array<Record<string, unknown>> }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/campaign-negative-targets`,
        body
      )
      .then((res) => res.data),

  createSbAdGroups: (
    profileId: string,
    body: { adGroups: Array<Record<string, unknown>> }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/ad-groups`, body)
      .then((res) => res.data),

  createSbProductCollectionAds: (
    profileId: string,
    body: {
      ads: Array<Record<string, unknown>>;
      /** autoCollection omits creative ASINs; productCollection requires exactly 3. */
      adFormat?: 'productCollection' | 'autoCollection';
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/ads`, body)
      .then((res) => res.data),

  createSdAdGroups: (
    profileId: string,
    body: { adGroups: Array<Record<string, unknown>> }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sd/ad-groups`, body)
      .then((res) => res.data),

  createSbThemes: (
    profileId: string,
    body: { themes: Array<Record<string, unknown>> }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/themes`, body)
      .then((res) => res.data),

  createSbTargets: (
    profileId: string,
    body: { targets: Array<Record<string, unknown>> }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/targets`, body)
      .then((res) => res.data),

  createSbKeywords: (
    profileId: string,
    keywords: Array<Record<string, unknown>>
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/keywords`, {
        keywords,
      })
      .then((res) => res.data),

  getBrands: (profileId: string): Promise<ApiResponse<unknown>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/brands`, { timeout: 20000 })
      .then((res) => res.data),

  listSbStores: (profileId: string, brandEntityId?: string): Promise<ApiResponse<unknown>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/stores`, {
        params: brandEntityId ? { brandEntityId } : undefined,
        timeout: 20000,
      })
      .then((res) => res.data),

  listSbStorePages: (
    profileId: string,
    storeId: string,
    brandEntityId?: string
  ): Promise<ApiResponse<unknown>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/stores/${encodeURIComponent(storeId)}/pages`,
        {
          params: brandEntityId ? { brandEntityId } : undefined,
          timeout: 20000,
        }
      )
      .then((res) => res.data),

  listSbStoreLogos: (
    profileId: string,
    brandEntityId?: string
  ): Promise<ApiResponse<{ assets: Array<{ assetId: string; url: string; mediaType: string; name: string }> }>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/store-assets`, {
        params: brandEntityId ? { brandEntityId } : undefined,
        timeout: 25000,
      })
      .then((res) => res.data),

  uploadSbStoreLogo: (
    profileId: string,
    file: File,
    brandEntityId?: string
  ): Promise<ApiResponse<{ assetId: string; url: string; mediaType: string; name: string }>> => {
    const form = new FormData();
    form.append('asset', file);
    if (brandEntityId) form.append('brandEntityId', brandEntityId);
    return api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/store-assets`, form)
      .then((res) => res.data);
  },

  listPortfolios: (profileId: string): Promise<ApiResponse<PortfoliosListResponse>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/portfolios`)
      .then((res) => res.data),

  updatePortfolioBudget: (
    profileId: string,
    portfolioId: string,
    body: { budget: number }
  ): Promise<ApiResponse<unknown>> =>
    api
      .put(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/portfolios/${encodeURIComponent(portfolioId)}/budget`,
        body
      )
      .then((res) => res.data),

  updateSpCampaignBudget: (
    profileId: string,
    campaignId: string,
    body: { budget: number; budgetType?: string }
  ): Promise<ApiResponse<unknown>> =>
    api
      .put(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/campaigns/${encodeURIComponent(campaignId)}/budget`,
        body
      )
      .then((res) => res.data),

  updateSbCampaignBudget: (
    profileId: string,
    campaignId: string,
    body: { budget: number }
  ): Promise<ApiResponse<unknown>> =>
    api
      .put(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/campaigns/${encodeURIComponent(campaignId)}/budget`,
        body
      )
      .then((res) => res.data),

  updateSdCampaignBudget: (
    profileId: string,
    campaignId: string,
    body: { budget: number }
  ): Promise<ApiResponse<unknown>> =>
    api
      .put(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sd/campaigns/${encodeURIComponent(campaignId)}/budget`,
        body
      )
      .then((res) => res.data),

  applyCampaignBudgetUpdates: (
    profileId: string,
    body: {
      updates: Array<{
        campaignId: string;
        adProduct: 'SP' | 'SB' | 'SD';
        budget: number;
        budgetType?: string;
        previousBudget?: number;
      }>;
    }
  ): Promise<ApiResponse<{ results?: Array<Record<string, unknown>> }>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/campaigns/budget/apply`,
        body
      )
      .then((res) => res.data),

  updateSpCampaignState: (
    profileId: string,
    campaignId: string,
    body: { state: 'ENABLED' | 'PAUSED' }
  ): Promise<ApiResponse<unknown>> =>
    api
      .put(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/campaigns/${encodeURIComponent(campaignId)}/state`,
        body
      )
      .then((res) => res.data),

  updateSbCampaignState: (
    profileId: string,
    campaignId: string,
    body: { state: 'ENABLED' | 'PAUSED' }
  ): Promise<ApiResponse<unknown>> =>
    api
      .put(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/campaigns/${encodeURIComponent(campaignId)}/state`,
        body
      )
      .then((res) => res.data),

  updateSdCampaignState: (
    profileId: string,
    campaignId: string,
    body: { state: 'ENABLED' | 'PAUSED' }
  ): Promise<ApiResponse<unknown>> =>
    api
      .put(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sd/campaigns/${encodeURIComponent(campaignId)}/state`,
        body
      )
      .then((res) => res.data),

  updateCampaignSettings: (
    profileId: string,
    campaignId: string,
    body: { ad_product: 'SP' | 'SB' | 'SD' } & CampaignSettingsPatch
  ): Promise<ApiResponse<unknown>> =>
    api
      .patch(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/campaigns/${encodeURIComponent(campaignId)}/settings`,
        body
      )
      .then((res) => res.data),

  updateSpAdGroupState: (
    profileId: string,
    adGroupId: string,
    body: { state: 'ENABLED' | 'PAUSED' }
  ): Promise<ApiResponse<unknown>> =>
    api
      .put(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/ad-groups/${encodeURIComponent(adGroupId)}/state`,
        body
      )
      .then((res) => res.data),

  updateSbAdGroupState: (
    profileId: string,
    adGroupId: string,
    body: { state: 'ENABLED' | 'PAUSED' }
  ): Promise<ApiResponse<unknown>> =>
    api
      .put(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/ad-groups/${encodeURIComponent(adGroupId)}/state`,
        body
      )
      .then((res) => res.data),

  updateSdAdGroupState: (
    profileId: string,
    adGroupId: string,
    body: { state: 'ENABLED' | 'PAUSED' }
  ): Promise<ApiResponse<unknown>> =>
    api
      .put(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sd/ad-groups/${encodeURIComponent(adGroupId)}/state`,
        body
      )
      .then((res) => res.data),

  updateSpProductAdsState: (
    profileId: string,
    body: {
      productAds: Array<{
        adId: string;
        state: 'ENABLED' | 'PAUSED';
      }>;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .put(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/product-ads/state`, body)
      .then((res) => res.data),

  getCampaignRecommendations: (
    profileId: string,
    params?: { startDate?: string; endDate?: string; adProducts?: string }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/campaigns/performance/recommendations`,
        { params, timeout: HEAVY_REQUEST_TIMEOUT_MS }
      )
      .then((res) => res.data),

  getKeywordRecommendations: (
    profileId: string,
    params?: {
      startDate?: string;
      endDate?: string;
      adProducts?: string;
      campaignId?: string;
      adGroupId?: string;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/keywords/performance/recommendations`,
        { params, timeout: HEAVY_REQUEST_TIMEOUT_MS }
      )
      .then((res) => res.data),

  getRuleRecommendationTables: (
    profileId: string,
    params?: {
      startDate?: string;
      endDate?: string;
      adProducts?: string;
      missedSpendMinRoas?: number;
      missedSpendMinOpportunity?: number;
      budgetUtilizationMin?: number;
      budgetIncreasePct?: number;
      tosImpressionFactor?: number;
      tosMaxShare?: number;
      poorKeywordMaxRoas?: number;
      poorKeywordMinClicks?: number;
      poorKeywordMaxOrders?: number;
      poorCampaignRoasFactor?: number;
      poorCampaignMinSpend?: number;
      chokedMinRating?: number;
      chokedMinUnits?: number;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/recommendations/rule-tables`, {
        params,
        timeout: HEAVY_REQUEST_TIMEOUT_MS,
      })
      .then((res) => res.data),

  applySpKeywords: (
    profileId: string,
    body: { actions: SpKeywordApplyAction[] }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/keywords/apply`, body)
      .then((res) => res.data),

  updateSpKeywordBid: (
    profileId: string,
    body: { keywordId: string; bid: number }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/keywords/apply`, {
        actions: [{ action: 'SET_BID', keywordId: body.keywordId, bid: body.bid }],
      })
      .then((res) => res.data),

  updateSbKeywordBid: (
    profileId: string,
    body: {
      keywordId: string;
      campaignId: string;
      adGroupId: string;
      bid: number;
      state?: string;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .put(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/keywords`, {
        keywords: [
          {
            keywordId: body.keywordId,
            campaignId: body.campaignId,
            adGroupId: body.adGroupId,
            bid: body.bid,
            state: body.state ?? 'enabled',
          },
        ],
      })
      .then((res) => res.data),

  updateSpTarget: (
    profileId: string,
    body: {
      targetId: string;
      bid?: number;
      state?: 'ENABLED' | 'PAUSED';
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .put(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/targets`, {
        targetingClauses: [
          {
            targetId: body.targetId,
            ...(body.bid != null ? { bid: body.bid } : {}),
            ...(body.state ? { state: body.state } : {}),
          },
        ],
      })
      .then((res) => res.data),

  updateSpTargetBid: (
    profileId: string,
    body: { targetId: string; bid: number }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    adsApi.updateSpTarget(profileId, { targetId: body.targetId, bid: body.bid }),

  updateSbTargetBid: (
    profileId: string,
    body: {
      targetId: string;
      campaignId: string;
      adGroupId: string;
      bid: number;
      state?: string;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .put(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sb/targets`, {
        targets: [
          {
            targetId: body.targetId,
            campaignId: body.campaignId,
            adGroupId: body.adGroupId,
            bid: body.bid,
            state: body.state ?? 'enabled',
          },
        ],
      })
      .then((res) => res.data),

  startSearchTermLastTwoMonthsReport: (
    profileId: string,
    body: { accountName?: string; managerAccountName?: string } = {}
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/search-terms/sync-last-two-months`,
        body
      )
      .then((res) => res.data),

  startSearchTermCurrentMonthReport: (
    profileId: string,
    body: { accountName?: string; managerAccountName?: string } = {}
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/search-terms/sync-current-month`,
        body
      )
      .then((res) => res.data),

  getSearchTermReportRows: (
    profileId: string,
    params?: {
      startDate?: string;
      endDate?: string;
      limit?: number;
      offset?: number;
      campaignIds?: string;
      adGroupIds?: string;
      keywordIds?: string;
      search?: string;
      matchType?: string;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/search-terms/rows`, {
        params,
      })
      .then((res) => res.data),

  getPlacementReportRows: (
    profileId: string,
    params?: { startDate?: string; endDate?: string }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/placements/rows`, {
        params,
      })
      .then((res) => res.data),

  createBudgetRules: (
    profileId: string,
    product: BudgetRuleApiProduct,
    body: {
      budgetRulesDetails: Array<Record<string, unknown>>;
      campaignId?: string;
      campaignIds?: string[];
    }
  ): Promise<ApiResponse<unknown>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/${product}/budget-rules`,
        body
      )
      .then((res) => res.data),

  getBudgetRules: (
    profileId: string,
    product: BudgetRuleApiProduct,
    pageSize = 30
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/${product}/budget-rules`, {
        params: { page_size: pageSize },
      })
      .then((res) => res.data),

  getBudgetRulesByCampaign: (
    profileId: string,
    product: BudgetRuleApiProduct,
    campaignId: string
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/${product}/campaigns/${encodeURIComponent(campaignId)}/budget-rules`
      )
      .then((res) => res.data),

  updateBudgetRules: (
    profileId: string,
    product: BudgetRuleApiProduct,
    body: { budgetRulesDetails: Array<Record<string, unknown>> }
  ): Promise<ApiResponse<unknown>> =>
    api
      .put(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/${product}/budget-rules`, body)
      .then((res) => res.data),

  getBudgetRuleCampaigns: (
    profileId: string,
    product: BudgetRuleApiProduct,
    ruleId: string,
    pageSize = 30
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/${product}/budget-rules/${encodeURIComponent(ruleId)}/campaigns`,
        { params: { page_size: pageSize } }
      )
      .then((res) => res.data),

  associateBudgetRulesToCampaign: (
    profileId: string,
    product: BudgetRuleApiProduct,
    campaignId: string,
    budgetRuleIds: string[]
  ): Promise<ApiResponse<unknown>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/${product}/campaigns/${encodeURIComponent(campaignId)}/budget-rules/associate`,
        { budgetRuleIds }
      )
      .then((res) => res.data),

  disassociateBudgetRuleFromCampaign: (
    profileId: string,
    product: BudgetRuleApiProduct,
    campaignId: string,
    ruleId: string
  ): Promise<ApiResponse<unknown>> =>
    api
      .delete(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/${product}/campaigns/${encodeURIComponent(campaignId)}/budget-rules/${encodeURIComponent(ruleId)}`
      )
      .then((res) => res.data),

  createSpBudgetRules: (
    profileId: string,
    body: { budgetRulesDetails: Array<Record<string, unknown>>; campaignId?: string }
  ): Promise<ApiResponse<unknown>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/budget-rules`, body)
      .then((res) => res.data),

  getSpBudgetRules: (
    profileId: string,
    pageSize = 30
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/budget-rules`, {
        params: { page_size: pageSize },
      })
      .then((res) => res.data),

  getSpBudgetRulesByCampaign: (
    profileId: string,
    campaignId: string
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/campaigns/${encodeURIComponent(campaignId)}/budget-rules`
      )
      .then((res) => res.data),

  updateSpBudgetRules: (
    profileId: string,
    body: { budgetRulesDetails: Array<Record<string, unknown>> }
  ): Promise<ApiResponse<unknown>> =>
    api
      .put(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sp/budget-rules`, body)
      .then((res) => res.data),

  startSpLastTwoMonthsReport: (
    profileId: string,
    body: { accountName?: string; managerAccountName?: string } = {}
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/sp/sync-last-two-months`, body)
      .then((res) => res.data),

  startSpCurrentMonthReport: (
    profileId: string,
    body: { accountName?: string; managerAccountName?: string } = {}
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/sp/sync-current-month`, body)
      .then((res) => res.data),

  startTargetLastTwoMonthsReport: (
    profileId: string,
    body: { accountName?: string; managerAccountName?: string } = {}
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/targets/sync-last-two-months`,
        body
      )
      .then((res) => res.data),

  startTargetCurrentMonthReport: (
    profileId: string,
    body: { accountName?: string; managerAccountName?: string } = {}
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/targets/sync-current-month`,
        body
      )
      .then((res) => res.data),

  startProfileReportSync: (
    profileId: string,
    body: { accountName?: string; managerAccountName?: string } = {}
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/sync`, body)
      .then((res) => res.data),

  startProfileUnifiedReportSync: (
    profileId: string,
    body: { accountName?: string; managerAccountName?: string } = {}
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/sync-from-s3`, body)
      .then((res) => res.data),

  getReportAnalysisInventory: (
    profileId: string,
    params?: { startMonth?: string; endMonth?: string }
  ): Promise<ApiResponse<ReportAnalysisInventory>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/analysis/inventory`, {
        params,
      })
      .then((res) => res.data),

  listReportAnalysisJobs: (profileId: string): Promise<ApiResponse<ReportAnalysisJob[]>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/analysis`)
      .then((res) => res.data),

  startReportAnalysis: (
    profileId: string,
    body: {
      startMonth?: string;
      endMonth?: string;
      brandNames?: string[];
      competitorNames?: string[];
    } = {}
  ): Promise<ApiResponse<ReportAnalysisJob>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/analysis`, body)
      .then((res) => res.data),

  uploadReportAnalysisZip: (
    profileId: string,
    file: File,
    fields: { startMonth?: string; endMonth?: string; brandNames?: string; competitorNames?: string }
  ): Promise<ApiResponse<ReportAnalysisJob>> => {
    const form = new FormData();
    form.append('file', file);
    if (fields.startMonth) form.append('start_month', fields.startMonth);
    if (fields.endMonth) form.append('end_month', fields.endMonth);
    if (fields.brandNames) form.append('brand_names', fields.brandNames);
    if (fields.competitorNames) form.append('competitor_names', fields.competitorNames);
    return api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/analysis/upload`, form, {
        timeout: HEAVY_REQUEST_TIMEOUT_MS,
      })
      .then((res) => res.data);
  },

  getShareOfVoiceKeywords: (profileId: string): Promise<ApiResponse<SovKeywordList>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sov/keywords`)
      .then((res) => res.data),

  addShareOfVoiceKeywords: (
    profileId: string,
    keywords: string[]
  ): Promise<ApiResponse<SovKeywordAddResult>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sov/keywords`, { keywords }, {
        timeout: 180_000,
      })
      .then((res) => res.data),

  removeShareOfVoiceKeyword: (
    profileId: string,
    keywordKey: string
  ): Promise<ApiResponse<{ keywordKey: string }>> =>
    api
      .delete(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sov/keywords/${encodeURIComponent(keywordKey)}`
      )
      .then((res) => res.data),

  getShareOfVoiceImportCandidates: (
    profileId: string
  ): Promise<ApiResponse<{ keywords: SovImportCandidate[] }>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sov/keywords/import-candidates`)
      .then((res) => res.data),

  getShareOfVoiceSummary: (
    profileId: string,
    params: { startDate: string; endDate: string }
  ): Promise<ApiResponse<SovSummary>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sov/keywords/summary`, { params })
      .then((res) => res.data),

  getShareOfVoiceKeyword: (
    profileId: string,
    keywordKey: string,
    params: { startDate: string; endDate: string }
  ): Promise<ApiResponse<SovKeywordDetail>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/sov/keywords/${encodeURIComponent(keywordKey)}`,
        { params }
      )
      .then((res) => res.data),

  listManualAnalysisAccounts: (): Promise<ApiResponse<ReportAnalysisConfig[]>> =>
    api.get('/api/v1/ads/reports/analysis/manual-accounts').then((res) => res.data),

  createManualAnalysisAccount: (body?: {
    brandNames?: string[];
    competitorNames?: string[];
    accountName?: string;
  }): Promise<ApiResponse<ReportAnalysisConfig>> =>
    api.post('/api/v1/ads/reports/analysis/manual-accounts', body ?? {}).then((res) => res.data),

  getReportAnalysisConfig: (profileId: string): Promise<ApiResponse<ReportAnalysisConfig>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/analysis/config`)
      .then((res) => res.data),

  putReportAnalysisConfig: (
    profileId: string,
    body: { brandNames: string[]; competitorNames: string[]; accountName?: string }
  ): Promise<ApiResponse<ReportAnalysisConfig>> =>
    api
      .put(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/analysis/config`, body)
      .then((res) => res.data),

  downloadReportAnalysisUrl: (profileId: string, jobId: string, format: 'xlsx' | 'pdf'): string =>
    `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/analysis/${encodeURIComponent(jobId)}/download?format=${format}`,

  retryReportAnalysisEmail: (
    profileId: string,
    jobId: string
  ): Promise<ApiResponse<ReportAnalysisJob>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/analysis/${encodeURIComponent(jobId)}/retry-email`
      )
      .then((res) => res.data),

  getReportBatchStatus: (
    profileId: string,
    batchId: string
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/batches/${encodeURIComponent(batchId)}`)
      .then((res) => res.data),

  getSpReportSummary: (
    profileId: string,
    params?: { startDate?: string; endDate?: string; adProducts?: string }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/sp/summary`, { params })
      .then((res) => res.data),

  getDailySpend: (profileId: string): Promise<ApiResponse<DailySpendPayload>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/daily-spend`)
      .then((res) => res.data),

  getProfileDailyExcel: (profileId: string): Promise<ApiResponse<ProfileDailyExcelConfig>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reporting/daily-excel`)
      .then((res) => res.data),

  updateProfileDailyExcelSchedule: (
    profileId: string,
    scheduleEnabled: boolean
  ): Promise<ApiResponse<ProfileDailyExcelConfig>> =>
    api
      .put(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reporting/daily-excel`, {
        scheduleEnabled,
      })
      .then((res) => res.data),

  getProfileDailyExcelPreview: (
    profileId: string
  ): Promise<ApiResponse<ProfileDailyExcelPreview>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reporting/daily-excel/preview`)
      .then((res) => res.data),

  sendProfileDailyExcel: (
    profileId: string
  ): Promise<ApiResponse<{ status: string; recipientCount?: number }>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reporting/daily-excel/send`)
      .then((res) => res.data),

  getReportCampaigns: (
    profileId: string,
    params?: { search?: string; segment?: string; adProduct?: string }
  ): Promise<ApiResponse<ReportCampaignList>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reporting/campaigns`, {
        params,
      })
      .then((res) => res.data),

  updateReportCampaignSegments: (
    profileId: string,
    body: {
      segment: ReportSegment | null;
      campaignKeys?: ReportCampaignKey[];
      match?: ReportCampaignMatch;
    }
  ): Promise<ApiResponse<{ updated: number; segment: ReportSegment | null }>> =>
    api
      .put(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reporting/campaign-segments`,
        body
      )
      .then((res) => res.data),

  getAmcContext: (profileId: string): Promise<ApiResponse<AmcContextPayload>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/amc/context`)
      .then((res) => res.data),

  syncAmcCatalog: (profileId: string): Promise<ApiResponse<AmcSyncPayload>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/amc/sync`)
      .then((res) => res.data),

  getAmcAudiences: (
    profileId: string,
    instanceId?: string
  ): Promise<ApiResponse<AmcAudiencesPayload>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/amc/audiences`, {
        params: instanceId ? { instanceId } : undefined,
      })
      .then((res) => res.data),

  getAmcAudience: (
    profileId: string,
    audienceExecutionId: string,
    instanceId?: string
  ): Promise<ApiResponse<AmcAudience>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/amc/audiences/${encodeURIComponent(audienceExecutionId)}`,
        { params: instanceId ? { instanceId } : undefined }
      )
      .then((res) => res.data),

  createAmcAudience: (
    profileId: string,
    body: CreateAmcAudienceBody
  ): Promise<ApiResponse<AmcAudience>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/amc/audiences`, body)
      .then((res) => res.data),

  createAmcLookalike: (
    profileId: string,
    body: CreateAmcLookalikeBody
  ): Promise<ApiResponse<AmcAudience>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/amc/audiences/lookalike`, body)
      .then((res) => res.data),

  updateAmcAudience: (
    profileId: string,
    audienceExecutionId: string,
    body: UpdateAmcAudienceBody,
    instanceId?: string
  ): Promise<ApiResponse<AmcAudience>> =>
    api
      .put(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/amc/audiences/${encodeURIComponent(audienceExecutionId)}`,
        body,
        { params: instanceId ? { instanceId } : undefined }
      )
      .then((res) => res.data),

  deleteAmcAudience: (
    profileId: string,
    audienceExecutionId: string,
    instanceId?: string
  ): Promise<ApiResponse<{ audienceExecutionId: string }>> =>
    api
      .delete(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/amc/audiences/${encodeURIComponent(audienceExecutionId)}`,
        { params: instanceId ? { instanceId } : undefined }
      )
      .then((res) => res.data),

  applyWorkspaceCampaignAudienceBidAdjustments: (
    profileId: string,
    body: {
      audienceId: string;
      percentage: number;
      campaigns: Array<{ campaignId: string; adProduct: 'SP' | 'SB' | 'SD' }>;
    }
  ): Promise<
    ApiResponse<{
      results?: Array<{
        campaignId?: string;
        adProduct?: string;
        status?: string;
        message?: string;
      }>;
      successCount?: number;
      failureCount?: number;
      skippedCount?: number;
      audienceId?: string;
      percentage?: number;
    }>
  > =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/workspace/campaigns/audience-bid-adjustments`,
        body
      )
      .then((res) => res.data),

  updateDailyBudget: (
    profileId: string,
    dailyBudget: number
  ): Promise<ApiResponse<DailySpendPayload>> =>
    api
      .put(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/daily-spend/budget`, {
        dailyBudget,
      })
      .then((res) => res.data),

  getSpReportSnapshot: (
    profileId: string,
    params?: {
      startDate?: string;
      endDate?: string;
      compareStartDate?: string;
      compareEndDate?: string;
      adProducts?: string;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/sp/snapshot`, { params })
      .then((res) => res.data),

  getTargetingTypeSummary: (
    profileId: string,
    params?: {
      startDate?: string;
      endDate?: string;
      compareStartDate?: string;
      compareEndDate?: string;
      adProducts?: string;
      campaignKeys?: string;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/targeting-type/summary`,
        { params }
      )
      .then((res) => res.data),

  getMatchTypeSummary: (
    profileId: string,
    params?: {
      startDate?: string;
      endDate?: string;
      compareStartDate?: string;
      compareEndDate?: string;
      adProducts?: string;
      campaignKeys?: string;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/match-type/summary`,
        { params }
      )
      .then((res) => res.data),

  getAdProductSummary: (
    profileId: string,
    params?: {
      startDate?: string;
      endDate?: string;
      compareStartDate?: string;
      compareEndDate?: string;
      adProducts?: string;
      campaignKeys?: string;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/ad-product/summary`,
        { params }
      )
      .then((res) => res.data),

  getPlacementSummary: (
    profileId: string,
    params?: {
      startDate?: string;
      endDate?: string;
      compareStartDate?: string;
      compareEndDate?: string;
      adProducts?: string;
      campaignKeys?: string;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/placement/summary`,
        { params, timeout: HEAVY_REQUEST_TIMEOUT_MS }
      )
      .then((res) => res.data),

  getSpReportRows: (
    profileId: string,
    params?: { startDate?: string; endDate?: string; adProducts?: string; limit?: number }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/sp/rows`, {
        params,
        timeout: HEAVY_REQUEST_TIMEOUT_MS,
      })
      .then((res) => res.data),

  getKeywordReportRows: (
    profileId: string,
    params?: {
      startDate?: string;
      endDate?: string;
      adProducts?: string;
      campaignId?: string;
      limit?: number;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/keywords/rows`, {
        params,
        timeout: HEAVY_REQUEST_TIMEOUT_MS,
      })
      .then((res) => res.data),

  getTargetReportRows: (
    profileId: string,
    params?: {
      startDate?: string;
      endDate?: string;
      adProducts?: string;
      campaignId?: string;
      limit?: number;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/reports/targets/rows`, {
        params,
        timeout: HEAVY_REQUEST_TIMEOUT_MS,
      })
      .then((res) => res.data),

  getStreamHealth: (
    profileId: string,
    params?: { queueUrl?: string }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/streams/health`, { params })
      .then((res) => res.data),

  getStreamSummary: (
    profileId: string,
    params?: { startTime?: string; endTime?: string; campaignIds?: string[]; portfolioIds?: string[] }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/streams/summary`, {
        params: streamAnalyticsQueryParams(params),
        timeout: 60000,
      })
      .then((res) => res.data),

  getStreamRows: (
    profileId: string,
    params?: {
      startTime?: string;
      endTime?: string;
      limit?: number;
      campaignIds?: string[];
      portfolioIds?: string[];
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/streams/rows`, {
        params: streamAnalyticsQueryParams(params),
      })
      .then((res) => res.data),

  getStreamHeatmap: (
    profileId: string,
    params?: {
      startTime?: string;
      endTime?: string;
      timezone?: string;
      days?: number;
      view?: 'normal' | 'average2w' | 'average4w' | 'average8w';
      campaignIds?: string[];
      portfolioIds?: string[];
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/streams/heatmap`, {
        params: streamAnalyticsQueryParams(params),
        timeout: 60000,
      })
      .then((res) => res.data),

  getStreamV2Summary: (
    profileId: string,
    params?: StreamAnalyticsQueryInput
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/streams/v2/summary`, {
        params: streamAnalyticsQueryParams(params),
        paramsSerializer: serializeStreamQueryParams,
        timeout: 60000,
      })
      .then((res) => res.data),

  getStreamV2Heatmap: (
    profileId: string,
    params?: StreamAnalyticsQueryInput
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/streams/v2/heatmap`, {
        params: streamAnalyticsQueryParams(params),
        paramsSerializer: serializeStreamQueryParams,
        timeout: 60000,
      })
      .then((res) => res.data),

  getStreamV2Keywords: (
    profileId: string,
    params?: { q?: string; campaignIds?: string[]; limit?: number }
  ): Promise<ApiResponse<{ keywords: StreamV2KeywordOption[] }>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/streams/v2/keywords`, {
        params: streamAnalyticsQueryParams(params),
      })
      .then((res) => res.data),

  getStreamV2SearchTerms: (
    profileId: string,
    params?: { q?: string; campaignIds?: string[]; keywordIds?: string[]; limit?: number }
  ): Promise<ApiResponse<{ searchTerms: string[] }>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/streams/v2/search-terms`, {
        params: streamAnalyticsQueryParams(params),
      })
      .then((res) => res.data),

  getStreamV2Asins: (
    profileId: string,
    params?: {
      q?: string;
      campaignIds?: string[];
      keywordIds?: string[];
      searchTerms?: string[];
      limit?: number;
    }
  ): Promise<ApiResponse<{ asins: string[] }>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/streams/v2/asins`, {
        params: streamAnalyticsQueryParams(params),
        paramsSerializer: serializeStreamQueryParams,
      })
      .then((res) => res.data),

  getStreamBudgetUsage: (
    profileId: string,
    params?: BudgetUsageQueryParams
  ): Promise<ApiResponse<BudgetUsageListResponse>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/streams/budget-usage`, {
        params,
      })
      .then((res) => res.data),

  createStreamSubscription: (
    profileId: string,
    body: {
      dataSetId: string;
      destinationArn?: string;
      queueUrl?: string;
      clientRequestToken?: string;
      notes?: string;
      realm?: string;
      awsRegion?: string;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/streams/subscriptions`, body)
      .then((res) => res.data),

  getStreamSubscriptions: (
    profileId: string,
    params?: { includeRemote?: boolean }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/streams/subscriptions`, { params })
      .then((res) => res.data),

  listBidSnapshots: (
    profileId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/bid-snapshots`, { params })
      .then((res) => res.data),

  createBidSnapshot: (
    profileId: string,
    body?: { name?: string; note?: string }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(`/api/v1/ads/profiles/${encodeURIComponent(profileId)}/bid-snapshots`, body ?? {})
      .then((res) => res.data),

  getBidSnapshot: (
    profileId: string,
    snapshotId: string
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/bid-snapshots/${encodeURIComponent(snapshotId)}`
      )
      .then((res) => res.data),

  listBidSnapshotItems: (
    profileId: string,
    snapshotId: string,
    params?: { entity_type?: string; search?: string; limit?: number; offset?: number }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/bid-snapshots/${encodeURIComponent(snapshotId)}/items`,
        { params }
      )
      .then((res) => res.data),

  retryBidSnapshotCapture: (
    profileId: string,
    snapshotId: string
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/bid-snapshots/${encodeURIComponent(snapshotId)}/retry-capture`
      )
      .then((res) => res.data),

  deleteBidSnapshot: (
    profileId: string,
    snapshotId: string
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .delete(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/bid-snapshots/${encodeURIComponent(snapshotId)}`
      )
      .then((res) => res.data),

  applyBidSnapshot: (
    profileId: string,
    snapshotId: string
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/bid-snapshots/${encodeURIComponent(snapshotId)}/apply`
      )
      .then((res) => res.data),

  listBidSnapshotApplyJobs: (
    profileId: string,
    snapshotId: string
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/bid-snapshots/${encodeURIComponent(snapshotId)}/apply-jobs`
      )
      .then((res) => res.data),

  getBidSnapshotApplyJob: (
    profileId: string,
    jobId: string
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/bid-snapshots/apply-jobs/${encodeURIComponent(jobId)}`
      )
      .then((res) => res.data),

  listBidSnapshotApplyResults: (
    profileId: string,
    jobId: string,
    params?: { status?: string; search?: string; limit?: number; offset?: number }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/bid-snapshots/apply-jobs/${encodeURIComponent(jobId)}/results`,
        { params }
      )
      .then((res) => res.data),

  retryBidSnapshotApply: (
    profileId: string,
    jobId: string
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/bid-snapshots/apply-jobs/${encodeURIComponent(jobId)}/retry`
      )
      .then((res) => res.data),

  exportBidSnapshotApplyResultsUrl: (profileId: string, jobId: string): string =>
    `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/bid-snapshots/apply-jobs/${encodeURIComponent(jobId)}/results/export`,

  createWorkspaceKeywordBidJob: (
    profileId: string,
    body: WorkspaceKeywordBidJobCreateBody
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/workspace/keywords/bid-jobs`,
        body
      )
      .then((res) => res.data),

  getWorkspaceKeywordBidJob: (
    profileId: string,
    jobId: string
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/workspace/keywords/bid-jobs/${encodeURIComponent(jobId)}`
      )
      .then((res) => res.data),

  listWorkspaceKeywordBidJobResults: (
    profileId: string,
    jobId: string,
    params?: { status?: string; search?: string; limit?: number; offset?: number }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/workspace/keywords/bid-jobs/${encodeURIComponent(jobId)}/results`,
        { params }
      )
      .then((res) => res.data),

  retryWorkspaceKeywordBidJob: (
    profileId: string,
    jobId: string
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/workspace/keywords/bid-jobs/${encodeURIComponent(jobId)}/retry`
      )
      .then((res) => res.data),

  createWorkspaceCampaignPlacementJob: (
    profileId: string,
    body: WorkspaceCampaignPlacementJobCreateBody
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/workspace/campaigns/placement-jobs`,
        body
      )
      .then((res) => res.data),

  getWorkspaceCampaignPlacementJob: (
    profileId: string,
    jobId: string
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/workspace/campaigns/placement-jobs/${encodeURIComponent(jobId)}`
      )
      .then((res) => res.data),

  listWorkspaceCampaignPlacementJobResults: (
    profileId: string,
    jobId: string,
    params?: { status?: string; search?: string; limit?: number; offset?: number }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/workspace/campaigns/placement-jobs/${encodeURIComponent(jobId)}/results`,
        { params }
      )
      .then((res) => res.data),

  retryWorkspaceCampaignPlacementJob: (
    profileId: string,
    jobId: string
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/workspace/campaigns/placement-jobs/${encodeURIComponent(jobId)}/retry`
      )
      .then((res) => res.data),

  createWorkspaceSearchTermNegateJob: (
    profileId: string,
    body: WorkspaceSearchTermNegateJobCreateBody
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/workspace/search-terms/negate-jobs`,
        body
      )
      .then((res) => res.data),

  getLatestWorkspaceSearchTermNegateJob: (
    profileId: string
  ): Promise<ApiResponse<Record<string, unknown> | null>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/workspace/search-terms/negate-jobs/latest`
      )
      .then((res) => res.data),

  getWorkspaceSearchTermNegateJob: (
    profileId: string,
    jobId: string
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/workspace/search-terms/negate-jobs/${encodeURIComponent(jobId)}`
      )
      .then((res) => res.data),

  listWorkspaceSearchTermNegateJobResults: (
    profileId: string,
    jobId: string,
    params?: { status?: string; search?: string; limit?: number; offset?: number }
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/workspace/search-terms/negate-jobs/${encodeURIComponent(jobId)}/results`,
        { params }
      )
      .then((res) => res.data),

  retryWorkspaceSearchTermNegateJob: (
    profileId: string,
    jobId: string
  ): Promise<ApiResponse<Record<string, unknown>>> =>
    api
      .post(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/workspace/search-terms/negate-jobs/${encodeURIComponent(jobId)}/retry`
      )
      .then((res) => res.data),
};

export const autoBiddingApi = {
  get: (
    profileId: string,
    campaignId: string,
    adProduct: 'SP' | 'SB'
  ): Promise<ApiResponse<AutoBiddingSettings>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/campaigns/${encodeURIComponent(campaignId)}/auto-bidding`,
        { params: { adProduct } }
      )
      .then((res) => res.data),

  save: (
    profileId: string,
    campaignId: string,
    adProduct: 'SP' | 'SB',
    body: AutoBiddingSavePayload
  ): Promise<ApiResponse<AutoBiddingSettings>> =>
    api
      .put(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/campaigns/${encodeURIComponent(campaignId)}/auto-bidding`,
        body,
        { params: { adProduct } }
      )
      .then((res) => res.data),

  disable: (
    profileId: string,
    campaignId: string,
    adProduct: 'SP' | 'SB'
  ): Promise<ApiResponse<AutoBiddingSettings>> =>
    api
      .put(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/campaigns/${encodeURIComponent(campaignId)}/auto-bidding/disable`,
        {},
        { params: { adProduct } }
      )
      .then((res) => res.data),

  preview: (
    profileId: string,
    campaignId: string,
    adProduct: 'SP' | 'SB'
  ): Promise<ApiResponse<AutoBiddingPreview>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/campaigns/${encodeURIComponent(campaignId)}/auto-bidding/preview`,
        { params: { adProduct } }
      )
      .then((res) => res.data),

  runLogs: (
    profileId: string,
    campaignId: string,
    adProduct: 'SP' | 'SB',
    limit = 50
  ): Promise<ApiResponse<AutoBiddingRunLog[]>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/campaigns/${encodeURIComponent(campaignId)}/auto-bidding/run-logs`,
        { params: { adProduct, limit } }
      )
      .then((res) => res.data),
};

export const outOfBudgetBoostApi = {
  getCampaign: (
    profileId: string,
    campaignId: string,
    adProduct: string
  ): Promise<ApiResponse<OutOfBudgetBoostSettings>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/campaigns/${encodeURIComponent(campaignId)}/out-of-budget-boost`,
        { params: { adProduct } }
      )
      .then((res) => res.data),

  saveCampaign: (
    profileId: string,
    campaignId: string,
    adProduct: string,
    body: OutOfBudgetBoostSavePayload
  ): Promise<ApiResponse<OutOfBudgetBoostSettings>> =>
    api
      .put(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/campaigns/${encodeURIComponent(campaignId)}/out-of-budget-boost`,
        body,
        { params: { adProduct } }
      )
      .then((res) => res.data),

  getPortfolio: (
    profileId: string,
    portfolioId: string
  ): Promise<ApiResponse<OutOfBudgetBoostSettings>> =>
    api
      .get(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/portfolios/${encodeURIComponent(portfolioId)}/out-of-budget-boost`
      )
      .then((res) => res.data),

  savePortfolio: (
    profileId: string,
    portfolioId: string,
    body: OutOfBudgetBoostSavePayload
  ): Promise<ApiResponse<OutOfBudgetBoostSettings>> =>
    api
      .put(
        `/api/v1/ads/profiles/${encodeURIComponent(profileId)}/portfolios/${encodeURIComponent(portfolioId)}/out-of-budget-boost`,
        body
      )
      .then((res) => res.data),
};

export const campaignsApi = {
  getCampaigns: (): Promise<ApiResponse<CampaignsApiResponse>> =>
    api.get('/api/v1/campaigns').then((res) => res.data),

  updateCampaignBudget: (campaignId: string, budgetUpdate: CampaignBudgetUpdate): Promise<ApiResponse<any>> =>
    api.put(`/api/v1/campaigns/${campaignId}/budget`, budgetUpdate).then((res) => res.data),

  updateCampaignHourlyBudget: (
    profileId: string,
    campaignRef: string,
    data: {
      hourly_budget: number | null;
      base_budget?: number | null;
      active?: boolean;
      base_windows?: {
        start_time_local: string;
        end_time_local: string;
        sort_order?: number;
        days_of_week?: number[];
      }[];
      metadata?: Record<string, unknown>;
      schedule_mode?: 'daily' | 'weekly';
      day_settings?: {
        day_of_week: number;
        hourly_budget: number | null;
        base_budget: number | null;
      }[];
      time_windows?: {
        start_time_local: string;
        end_time_local: string;
        multiplier: number;
        sort_order?: number;
        days_of_week?: number[];
      }[];
    },
    options?: { product?: 'SP' | 'SB' | 'SD' }
  ): Promise<ApiResponse<any>> =>
    api
      .put(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/campaigns/${encodeURIComponent(campaignRef)}/hourly-budget`,
        data,
        { params: options?.product ? { product: options.product } : undefined }
      )
      .then((res) => res.data),

  getCampaignHourlyBudget: (
    profileId: string,
    campaignRef: string,
    options?: { product?: 'SP' | 'SB' | 'SD' }
  ): Promise<ApiResponse<any>> =>
    api
      .get(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/campaigns/${encodeURIComponent(campaignRef)}/hourly-budget`,
        { params: options?.product ? { product: options.product } : undefined }
      )
      .then((res) => res.data),

  deleteCampaignHourlyBudget: (
    profileId: string,
    campaignRef: string,
    options?: { product?: 'SP' | 'SB' | 'SD' }
  ): Promise<ApiResponse<null>> =>
    api
      .delete(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/campaigns/${encodeURIComponent(campaignRef)}/hourly-budget`,
        { params: options?.product ? { product: options.product } : undefined }
      )
      .then((res) => res.data),

  getHourlyBudgetRunLogs: (
    profileId: string,
    campaignRef: string,
    params?: { limit?: number; product?: 'SP' | 'SB' | 'SD' }
  ): Promise<ApiResponse<Record<string, unknown>[]>> =>
    api
      .get(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/campaigns/${encodeURIComponent(campaignRef)}/hourly-budget/run-logs`,
        {
          params:
            params?.limit != null || params?.product
              ? {
                  ...(params?.limit != null ? { limit: params.limit } : {}),
                  ...(params?.product ? { product: params.product } : {}),
                }
              : undefined,
        }
      )
      .then((res) => res.data),

  listHourlyBudgets: (
    profileId: string,
    params?: HourlyBudgetListParams
  ): Promise<ApiResponse<HourlyBudgetListData>> =>
    api
      .get(`/api/v1/profiles/${encodeURIComponent(profileId)}/hourly-budgets`, {
        params: {
          ...(params?.q?.trim() ? { q: params.q.trim() } : {}),
          ...(params?.active != null ? { active: params.active } : {}),
          ...(params?.product ? { product: params.product } : {}),
          ...(params?.kind ? { kind: params.kind } : {}),
          ...(params?.limit != null ? { limit: params.limit } : {}),
          ...(params?.offset != null ? { offset: params.offset } : {}),
        },
      })
      .then((res) => res.data),

  listMissingBaseBudgets: (
    profileId: string,
    params?: { q?: string; product?: HourlyBudgetListParams['product'] }
  ): Promise<ApiResponse<MissingBaseBudgetListData>> =>
    api
      .get(`/api/v1/profiles/${encodeURIComponent(profileId)}/hourly-budgets/missing-base`, {
        params: {
          ...(params?.q?.trim() ? { q: params.q.trim() } : {}),
          ...(params?.product ? { product: params.product } : {}),
        },
      })
      .then((res) => res.data),

  setHourlyBudgetBase: (
    profileId: string,
    data: SetBaseBudgetPayload
  ): Promise<ApiResponse<{ campaign_id: string; base_budget: number; created: boolean }>> =>
    api
      .put(`/api/v1/profiles/${encodeURIComponent(profileId)}/hourly-budgets/base`, data)
      .then((res) => res.data),

  createHourlyBudgetGroup: (
    profileId: string,
    data: HourlyBudgetGroupPayload
  ): Promise<ApiResponse<HourlyBudgetListItem>> =>
    api
      .post(`/api/v1/profiles/${encodeURIComponent(profileId)}/hourly-budget-groups`, data)
      .then((res) => res.data),

  updateHourlyBudgetGroup: (
    profileId: string,
    groupId: string,
    data: HourlyBudgetGroupPayload
  ): Promise<ApiResponse<HourlyBudgetListItem>> =>
    api
      .put(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/hourly-budget-groups/${encodeURIComponent(groupId)}`,
        data
      )
      .then((res) => res.data),

  deleteHourlyBudgetGroup: (profileId: string, groupId: string): Promise<ApiResponse<null>> =>
    api
      .delete(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/hourly-budget-groups/${encodeURIComponent(groupId)}`
      )
      .then((res) => res.data),

  previewSmartBudget: (
    profileId: string,
    campaignRef: string,
    data: { daily_budget: number }
  ): Promise<ApiResponse<SmartBudgetPlan>> =>
    api
      .post(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/campaigns/${encodeURIComponent(campaignRef)}/smart-budget/preview`,
        data,
        { timeout: HEAVY_REQUEST_TIMEOUT_MS }
      )
      .then((res) => res.data),

  applySmartBudget: (
    profileId: string,
    campaignRef: string,
    data: { daily_budget: number }
  ): Promise<ApiResponse<SmartBudgetPlan>> =>
    api
      .post(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/campaigns/${encodeURIComponent(campaignRef)}/smart-budget/apply`,
        data,
        { timeout: HEAVY_REQUEST_TIMEOUT_MS }
      )
      .then((res) => res.data),
};

export const flagsApi = {
  preview: (
    profileId: string,
    data: FlagPreviewRequest
  ): Promise<ApiResponse<FlagPreviewData>> =>
    api
      .post(`/api/v1/profiles/${encodeURIComponent(profileId)}/flags/preview`, data)
      .then((res) => res.data),
};

export const chatApi = {
  createThread: (
    profileId: string,
    body?: { title?: string }
  ): Promise<ApiResponse<import('@/types/chat').ChatThread>> =>
    api
      .post(`/api/v1/profiles/${encodeURIComponent(profileId)}/chat/threads`, body ?? {})
      .then((res) => res.data),

  listThreads: (
    profileId: string,
    params?: { limit?: number }
  ): Promise<ApiResponse<{ threads: import('@/types/chat').ChatThread[] }>> =>
    api
      .get(`/api/v1/profiles/${encodeURIComponent(profileId)}/chat/threads`, { params })
      .then((res) => res.data),

  listMessages: (
    profileId: string,
    threadId: string,
    params?: { limit?: number }
  ): Promise<ApiResponse<{ messages: import('@/types/chat').ChatMessage[] }>> =>
    api
      .get(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/chat/threads/${encodeURIComponent(threadId)}/messages`,
        { params }
      )
      .then((res) => res.data),

  sendMessage: (
    profileId: string,
    threadId: string,
    body: { content: string; timezone?: string }
  ): Promise<ApiResponse<import('@/types/chat').ChatSendResponse>> =>
    api
      .post(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/chat/threads/${encodeURIComponent(threadId)}/messages`,
        body,
        { timeout: 60000 }
      )
      .then((res) => res.data),

  archiveThread: (
    profileId: string,
    threadId: string
  ): Promise<ApiResponse<{ archived: boolean }>> =>
    api
      .delete(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/chat/threads/${encodeURIComponent(threadId)}`
      )
      .then((res) => res.data),

  getAllowlist: (
    profileId: string
  ): Promise<ApiResponse<{ enabled: boolean; action_types: string[] }>> =>
    api
      .get(`/api/v1/profiles/${encodeURIComponent(profileId)}/chat/llm-action-allowlist`)
      .then((res) => res.data),

  listActions: (
    profileId: string,
    params?: { status?: string; action_type?: string; limit?: number }
  ): Promise<ApiResponse<{ items: import('@/types/chat').ChatActionHistoryItem[] }>> =>
    api
      .get(`/api/v1/profiles/${encodeURIComponent(profileId)}/chat/actions`, { params })
      .then((res) => res.data),

  getProposal: (
    profileId: string,
    threadId: string,
    proposalId: string
  ): Promise<ApiResponse<import('@/types/chat').ChatActionProposal>> =>
    api
      .get(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/chat/threads/${encodeURIComponent(threadId)}/proposals/${encodeURIComponent(proposalId)}`
      )
      .then((res) => res.data),

  patchProposal: (
    profileId: string,
    threadId: string,
    proposalId: string,
    body: { selected_item_ids?: string[]; params?: Record<string, unknown> }
  ): Promise<ApiResponse<import('@/types/chat').ChatActionProposal>> =>
    api
      .patch(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/chat/threads/${encodeURIComponent(threadId)}/proposals/${encodeURIComponent(proposalId)}`,
        body
      )
      .then((res) => res.data),

  confirmProposal: (
    profileId: string,
    threadId: string,
    proposalId: string,
    body?: { idempotency_key?: string }
  ): Promise<ApiResponse<import('@/types/chat').ChatActionProposal>> =>
    api
      .post(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/chat/threads/${encodeURIComponent(threadId)}/proposals/${encodeURIComponent(proposalId)}/confirm`,
        body ?? {},
        { timeout: 120000 }
      )
      .then((res) => res.data),

  rejectProposal: (
    profileId: string,
    threadId: string,
    proposalId: string
  ): Promise<ApiResponse<import('@/types/chat').ChatActionProposal>> =>
    api
      .post(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/chat/threads/${encodeURIComponent(threadId)}/proposals/${encodeURIComponent(proposalId)}/reject`
      )
      .then((res) => res.data),

  undoProposal: (
    profileId: string,
    threadId: string,
    proposalId: string
  ): Promise<ApiResponse<import('@/types/chat').ChatActionProposal>> =>
    api
      .post(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/chat/threads/${encodeURIComponent(threadId)}/proposals/${encodeURIComponent(proposalId)}/undo`
      )
      .then((res) => res.data),
};

export type AmazonOauthConnection = {
  id: string;
  manager_account_id: string | null;
  display_name: string | null;
  scopes: string | null;
  consent_granted_at: string | null;
  expires_at: string | null;
  days_until_expiry: number | null;
  expiry_warning_tier: string | null;
  last_api_success_at: string | null;
  status: string;
  health: 'active' | 'expiring_soon' | 'reauth_required' | 'inactive_risk' | string;
  realm?: 'NA' | 'EU' | 'FE' | string | null;
  profile_ids: string[];
  created_by_email?: string | null;
};

export type AmazonOauthAlert = {
  id: string;
  credential_id: string;
  alert_type: string;
  title: string;
  body: string | null;
  created_at: string | null;
};

export const amazonOauthApi = {
  getAuthorizeUrl: (
    credentialId?: string | null,
    realm?: string | null
  ): Promise<ApiResponse<{ authorize_url: string; state: string; redirect_uri: string; realm?: string }>> =>
    api
      .get('/api/v1/amazon/oauth/authorize', {
        params: {
          ...(credentialId ? { credential_id: credentialId } : {}),
          ...(realm ? { realm } : {}),
        },
      })
      .then((res) => res.data),

  listConnections: (): Promise<
    ApiResponse<{ connections: AmazonOauthConnection[]; alerts: AmazonOauthAlert[] }>
  > => api.get('/api/v1/amazon/oauth/connections').then((res) => res.data),

  reconnect: (
    credentialId: string
  ): Promise<ApiResponse<{ authorize_url: string; state: string; redirect_uri: string }>> =>
    api
      .post(`/api/v1/amazon/oauth/connections/${encodeURIComponent(credentialId)}/reconnect`)
      .then((res) => res.data),

  markAlertRead: (alertId: string): Promise<ApiResponse<unknown>> =>
    api.post(`/api/v1/amazon/oauth/alerts/${encodeURIComponent(alertId)}/read`).then((res) => res.data),
};

export type FlipkartOauthConnection = {
  id: string;
  seller_id: string | null;
  display_name: string | null;
  created_by_user_id?: string | null;
  created_by_email?: string | null;
  scopes: string | null;
  consent_granted_at: string | null;
  expires_at: string | null;
  access_token_expires_at: string | null;
  days_until_expiry: number | null;
  expiry_warning_tier: string | null;
  last_api_success_at: string | null;
  status: string;
  health: 'active' | 'expiring_soon' | 'reauth_required' | 'inactive_risk' | string;
};

export const flipkartOauthApi = {
  getAuthorizeUrl: (
    credentialId?: string | null
  ): Promise<ApiResponse<{ authorize_url: string; state: string; redirect_uri: string }>> =>
    api
      .get('/api/v1/flipkart/oauth/authorize', {
        params: credentialId ? { credential_id: credentialId } : undefined,
      })
      .then((res) => res.data),

  listConnections: (): Promise<ApiResponse<{ connections: FlipkartOauthConnection[]; alerts: unknown[] }>> =>
    api.get('/api/v1/flipkart/oauth/connections').then((res) => res.data),

  reconnect: (
    credentialId: string
  ): Promise<ApiResponse<{ authorize_url: string; state: string; redirect_uri: string }>> =>
    api
      .post(`/api/v1/flipkart/oauth/connections/${encodeURIComponent(credentialId)}/reconnect`)
      .then((res) => res.data),

  verify: (credentialId: string): Promise<ApiResponse<{ ok: boolean; status: number; sample: unknown }>> =>
    api
      .get(`/api/v1/flipkart/oauth/connections/${encodeURIComponent(credentialId)}/verify`)
      .then((res) => res.data),
};

export type FlipkartAdsOauthConnection = {
  id: string;
  advertiser_id: string | null;
  display_name: string | null;
  created_by_user_id: string | null;
  created_by_email?: string | null;
  scopes: string | null;
  consent_granted_at: string | null;
  expires_at: string | null;
  access_token_expires_at: string | null;
  days_until_expiry: number | null;
  expiry_warning_tier: string | null;
  last_refreshed_at: string | null;
  last_api_success_at: string | null;
  status: string;
  health: 'active' | 'expiring_soon' | 'reauth_required' | 'inactive_risk' | string;
  created_at: string | null;
};

export type FlipkartAdsOauthBootstrap = {
  client_id: string;
  redirect_uri: string;
  state: string;
  scope: string;
  sdk_script_url: string;
};

export type FlipkartAdsOauthExchangeResult = {
  credential_id: string;
  advertiser_id: string | null;
  expires_at: string | null;
  status: string;
  reused?: boolean;
};

export const flipkartAdsOauthApi = {
  getAuthorize: (
    credentialId?: string | null
  ): Promise<ApiResponse<FlipkartAdsOauthBootstrap>> =>
    api
      .get('/api/v1/flipkart-ads/oauth/authorize', {
        params: credentialId ? { credential_id: credentialId } : undefined,
      })
      .then((res) => res.data),

  exchange: (body: {
    code: string;
    state?: string | null;
    credential_id?: string | null;
  }): Promise<ApiResponse<FlipkartAdsOauthExchangeResult>> =>
    api.post('/api/v1/flipkart-ads/oauth/exchange', body).then((res) => res.data),

  listConnections: (): Promise<
    ApiResponse<{ connections: FlipkartAdsOauthConnection[]; alerts: unknown[] }>
  > => api.get('/api/v1/flipkart-ads/oauth/connections').then((res) => res.data),
};

export type FlipkartAdsAdAccount = {
  id: string;
  credential_id: string;
  business_account_id: string;
  ad_account_id: string;
  name: string | null;
  status: string | null;
  marketplace: string | null;
  synced_at: string | null;
  updated_at: string | null;
};

export type FlipkartAdsCampaign = {
  id: string;
  credential_id: string;
  advertiser_id: string | null;
  business_account_id?: string | null;
  ad_account_id?: string | null;
  campaign_id: string;
  name: string | null;
  status: string | null;
  campaign_type: string | null;
  daily_budget: number | null;
  start_date: string | null;
  end_date: string | null;
  synced_at: string | null;
  updated_at: string | null;
};

export type FlipkartCampaignDuplicateResult = {
  campaign_id: string;
  name: string;
  status: string;
  failed_step: { step: string; message: string } | null;
};

export type FlipkartAdsPlacementCost = {
  type?: string | null;
  absoluteCost?: number | null;
  percentage?: number | null;
};

export type FlipkartAdsAdGroupBanner = {
  id?: string | null;
  productId?: string | null;
};

export type FlipkartAdsAdGroup = {
  id: string;
  credential_id: string;
  business_account_id: string | null;
  ad_account_id: string | null;
  campaign_id: string | null;
  ad_group_id: string;
  name: string | null;
  status: string | null;
  ui_status: string | null;
  product_count: number | null;
  commodity_id: number | null;
  cost_model: string | null;
  cost: number | null;
  bid: number | null;
  budget: number | null;
  budget_type: string | null;
  banners: FlipkartAdsAdGroupBanner[];
  store_paths: string[];
  cost_variation: { placements?: FlipkartAdsPlacementCost[] } | null;
  pricing_currency: string | null;
  indicative_price: number | null;
  ceiling_price: number | null;
  floor_price: number | null;
  winning_min_cpx: number | null;
  winning_max_cpx: number | null;
  winning_avg_cpx: number | null;
  synced_at: string | null;
  updated_at: string | null;
};

export type FlipkartPlacementMode =
  | 'set_inr'
  | 'increase_pct'
  | 'decrease_pct'
  | 'increase_inr'
  | 'decrease_inr';

export type FlipkartPlacementBidsResult = {
  applied: { ad_group_id: string; placements: Record<string, number> }[];
  skipped: { ad_group_id: string; reason: string }[];
  failed: { ad_group_id: string; reason: string; message?: string | null }[];
};

export type FlipkartAdsKeyword = {
  id: string;
  credential_id: string;
  business_account_id: string | null;
  ad_account_id: string | null;
  campaign_id: string | null;
  ad_group_id: string | null;
  keyword_id: string;
  keyword_text: string | null;
  match_type: string | null;
  status: string | null;
  bid: number | null;
  synced_at: string | null;
  updated_at: string | null;
};

export type FlipkartAdsNegativeKeyword = {
  id: string;
  credential_id: string;
  business_account_id: string | null;
  ad_account_id: string | null;
  campaign_id: string | null;
  ad_group_id: string | null;
  negative_keyword_id: string;
  keyword_text: string | null;
  match_type: string | null;
  status: string | null;
  synced_at: string | null;
  updated_at: string | null;
};

export type FlipkartAdsProduct = {
  id: string;
  credential_id: string;
  business_account_id: string | null;
  ad_account_id: string | null;
  campaign_id: string | null;
  ad_group_id: string | null;
  product_id: string;
  fsn: string | null;
  sku: string | null;
  name: string | null;
  status: string | null;
  image_url: string | null;
  brand: string | null;
  min_price: number | null;
  max_price: number | null;
  currency: string | null;
  store_list: string[];
  synced_at: string | null;
  updated_at: string | null;
};

export type FlipkartAdsReportGrain =
  | 'campaigns'
  | 'ad-groups'
  | 'keywords'
  | 'search-terms'
  | 'products';

export type FlipkartAdsReportRow = {
  id: string;
  credential_id: string;
  ad_account_id: string | null;
  business_account_id: string | null;
  date: string | null;
  campaign_id: string | null;
  campaign_type: string | null;
  campaign_name?: string | null;
  ad_group_id?: string | null;
  ad_group_name?: string | null;
  keyword_id?: string | null;
  keyword_text?: string | null;
  match_type?: string | null;
  search_term?: string | null;
  product_id?: string | null;
  fsn?: string | null;
  impressions: number;
  clicks: number;
  spend: number;
  revenue: number;
  orders: number;
  units: number;
  roi: number | null;
  updated_at: string | null;
};

export type FlipkartAdsEntitySyncCount = {
  fetched: number;
  upserted: number;
};

export type FlipkartAdsEntitySyncStatus = {
  available_at: string | null;
  retry_after_seconds: number;
};

export type FlipkartAdsEntitySyncResult = {
  accounts: { fetched: number; upserted: number; advertiser_id: string | null };
  campaigns: {
    fetched: number;
    upserted: number;
    advertiser_id: string | null;
    accounts?: number;
  };
  ad_groups: FlipkartAdsEntitySyncCount;
  keywords: FlipkartAdsEntitySyncCount;
  negative_keywords: FlipkartAdsEntitySyncCount;
  products: FlipkartAdsEntitySyncCount;
  pricing?: { fetched: number };
};

export type FlipkartAdsReportGrainSyncCount = {
  fetched: number;
  upserted: number;
  statuses: string[];
};

export type FlipkartAdsReportSyncResult = {
  window_start: string;
  window_end: string;
  grains: Record<string, FlipkartAdsReportGrainSyncCount>;
};

const FLIPKART_ADS_SYNC_TIMEOUT_MS = 180_000;

function flipkartAdsAdAccountParams(adAccountId?: string) {
  return adAccountId ? { ad_account_id: adAccountId } : undefined;
}

export const flipkartAdsApi = {
  listAllAccounts: (): Promise<ApiResponse<{ accounts: FlipkartAdsAdAccount[] }>> =>
    api.get('/api/v1/flipkart-ads/accounts').then((res) => res.data),

  listAccounts: (
    credentialId: string
  ): Promise<ApiResponse<{ accounts: FlipkartAdsAdAccount[] }>> =>
    api
      .get(`/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/accounts`)
      .then((res) => res.data),

  syncAccounts: (
    credentialId: string
  ): Promise<ApiResponse<{ fetched: number; upserted: number; advertiser_id: string | null }>> =>
    api
      .post(`/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/sync-accounts`)
      .then((res) => res.data),

  listCampaigns: (
    credentialId: string,
    params?: { adAccountId?: string }
  ): Promise<ApiResponse<{ campaigns: FlipkartAdsCampaign[] }>> =>
    api
      .get(`/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/campaigns`, {
        params: flipkartAdsAdAccountParams(params?.adAccountId),
      })
      .then((res) => res.data),

  syncCampaigns: (
    credentialId: string
  ): Promise<
    ApiResponse<{
      fetched: number;
      upserted: number;
      advertiser_id: string | null;
      accounts?: number;
    }>
  > =>
    api
      .post(`/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/sync-campaigns`)
      .then((res) => res.data),

  updateCampaign: (
    credentialId: string,
    campaignId: string,
    body: {
      daily_budget?: number;
      start_date?: string | null;
      end_date?: string | null;
    }
  ): Promise<ApiResponse<FlipkartAdsCampaign>> =>
    api
      .patch(
        `/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/campaigns/${encodeURIComponent(campaignId)}`,
        body
      )
      .then((res) => res.data),

  duplicateCampaign: (
    credentialId: string,
    campaignId: string,
    body: {
      name: string;
      start_date: string;
      end_date: string | null;
      product_ids: string[];
      make_live: boolean;
    }
  ): Promise<ApiResponse<FlipkartCampaignDuplicateResult>> =>
    api
      .post(
        `/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/campaigns/${encodeURIComponent(campaignId)}/duplicate`,
        body,
        { timeout: FLIPKART_ADS_SYNC_TIMEOUT_MS }
      )
      .then((res) => res.data),

  createCampaignEndDateJob: (
    credentialId: string,
    body: {
      ad_account_id: string;
      campaign_ids: string[];
      end_date: string | null;
    }
  ): Promise<ApiResponse<unknown>> =>
    api
      .post(
        `/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/campaigns/end-date-jobs`,
        body
      )
      .then((res) => res.data),

  getCampaignEndDateJob: (
    credentialId: string,
    jobId: string
  ): Promise<ApiResponse<unknown>> =>
    api
      .get(
        `/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/campaigns/end-date-jobs/${encodeURIComponent(jobId)}`
      )
      .then((res) => res.data),

  listCampaignEndDateJobResults: (
    credentialId: string,
    jobId: string,
    params?: { status?: string; limit?: number; offset?: number }
  ): Promise<ApiResponse<{ results?: unknown[] }>> =>
    api
      .get(
        `/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/campaigns/end-date-jobs/${encodeURIComponent(jobId)}/results`,
        { params }
      )
      .then((res) => res.data),

  retryCampaignEndDateJob: (
    credentialId: string,
    jobId: string
  ): Promise<ApiResponse<unknown>> =>
    api
      .post(
        `/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/campaigns/end-date-jobs/${encodeURIComponent(jobId)}/retry`
      )
      .then((res) => res.data),

  createCampaignBudgetJob: (
    credentialId: string,
    body: {
      ad_account_id: string;
      campaign_ids: string[];
      adjust_mode: 'INCREASE_AMOUNT' | 'INCREASE_PERCENT';
      adjust_value: number;
    }
  ): Promise<ApiResponse<unknown>> =>
    api
      .post(
        `/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/campaigns/budget-jobs`,
        body
      )
      .then((res) => res.data),

  getCampaignBudgetJob: (
    credentialId: string,
    jobId: string
  ): Promise<ApiResponse<unknown>> =>
    api
      .get(
        `/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/campaigns/budget-jobs/${encodeURIComponent(jobId)}`
      )
      .then((res) => res.data),

  listCampaignBudgetJobResults: (
    credentialId: string,
    jobId: string,
    params?: { status?: string; limit?: number; offset?: number }
  ): Promise<ApiResponse<{ results?: unknown[] }>> =>
    api
      .get(
        `/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/campaigns/budget-jobs/${encodeURIComponent(jobId)}/results`,
        { params }
      )
      .then((res) => res.data),

  retryCampaignBudgetJob: (
    credentialId: string,
    jobId: string
  ): Promise<ApiResponse<unknown>> =>
    api
      .post(
        `/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/campaigns/budget-jobs/${encodeURIComponent(jobId)}/retry`
      )
      .then((res) => res.data),

  entitySyncStatus: (
    credentialId: string
  ): Promise<ApiResponse<FlipkartAdsEntitySyncStatus>> =>
    api
      .get(`/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/sync-entities`)
      .then((res) => res.data),

  syncEntities: (
    credentialId: string
  ): Promise<ApiResponse<FlipkartAdsEntitySyncResult>> =>
    api
      .post(
        `/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/sync-entities`,
        undefined,
        { timeout: FLIPKART_ADS_SYNC_TIMEOUT_MS }
      )
      .then((res) => res.data),

  syncReports: (
    credentialId: string
  ): Promise<ApiResponse<FlipkartAdsReportSyncResult>> =>
    api
      .post(
        `/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/sync-reports`,
        undefined,
        { timeout: FLIPKART_ADS_SYNC_TIMEOUT_MS }
      )
      .then((res) => res.data),

  listAdGroups: (
    credentialId: string,
    params?: { adAccountId?: string }
  ): Promise<ApiResponse<{ ad_groups: FlipkartAdsAdGroup[] }>> =>
    api
      .get(`/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/ad-groups`, {
        params: flipkartAdsAdAccountParams(params?.adAccountId),
      })
      .then((res) => res.data),

  updateAdGroupPlacements: (
    credentialId: string,
    body: {
      ad_account_id: string;
      ad_group_ids: string[];
      placements: Record<string, { mode: FlipkartPlacementMode; value: number }>;
    }
  ): Promise<ApiResponse<FlipkartPlacementBidsResult>> =>
    api
      .post(
        `/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/ad-groups/placement-bids`,
        body
      )
      .then((res) => res.data),

  listKeywords: (
    credentialId: string,
    params?: { adAccountId?: string }
  ): Promise<ApiResponse<{ keywords: FlipkartAdsKeyword[] }>> =>
    api
      .get(`/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/keywords`, {
        params: flipkartAdsAdAccountParams(params?.adAccountId),
      })
      .then((res) => res.data),

  listNegativeKeywords: (
    credentialId: string,
    params?: { adAccountId?: string }
  ): Promise<ApiResponse<{ negative_keywords: FlipkartAdsNegativeKeyword[] }>> =>
    api
      .get(
        `/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/negative-keywords`,
        { params: flipkartAdsAdAccountParams(params?.adAccountId) }
      )
      .then((res) => res.data),

  listProducts: (
    credentialId: string,
    params?: { adAccountId?: string }
  ): Promise<ApiResponse<{ products: FlipkartAdsProduct[] }>> =>
    api
      .get(`/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/products`, {
        params: flipkartAdsAdAccountParams(params?.adAccountId),
      })
      .then((res) => res.data),

  listReports: (
    credentialId: string,
    grain: FlipkartAdsReportGrain,
    params?: { adAccountId?: string }
  ): Promise<ApiResponse<{ grain: string; rows: FlipkartAdsReportRow[] }>> =>
    api
      .get(
        `/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/reports/${encodeURIComponent(grain)}`,
        { params: flipkartAdsAdAccountParams(params?.adAccountId) }
      )
      .then((res) => res.data),

  getDailyExcel: (
    credentialId: string,
    adAccountId: string
  ): Promise<ApiResponse<ProfileDailyExcelConfig>> =>
    api
      .get(
        `/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/accounts/${encodeURIComponent(adAccountId)}/reporting/daily-excel`
      )
      .then((res) => res.data),

  updateDailyExcelSchedule: (
    credentialId: string,
    adAccountId: string,
    scheduleEnabled: boolean
  ): Promise<ApiResponse<ProfileDailyExcelConfig>> =>
    api
      .put(
        `/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/accounts/${encodeURIComponent(adAccountId)}/reporting/daily-excel`,
        { scheduleEnabled }
      )
      .then((res) => res.data),

  getDailyExcelPreview: (
    credentialId: string,
    adAccountId: string
  ): Promise<ApiResponse<ProfileDailyExcelPreview>> =>
    api
      .get(
        `/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/accounts/${encodeURIComponent(adAccountId)}/reporting/daily-excel/preview`
      )
      .then((res) => res.data),

  sendDailyExcel: (
    credentialId: string,
    adAccountId: string
  ): Promise<ApiResponse<{ status: string; recipientCount?: number }>> =>
    api
      .post(
        `/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/accounts/${encodeURIComponent(adAccountId)}/reporting/daily-excel/send`
      )
      .then((res) => res.data),

  listReportCampaigns: (
    credentialId: string,
    adAccountId: string,
    params?: { search?: string; segment?: string; adProduct?: string }
  ): Promise<ApiResponse<ReportCampaignList>> =>
    api
      .get(
        `/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/accounts/${encodeURIComponent(adAccountId)}/reporting/campaigns`,
        { params }
      )
      .then((res) => res.data),

  updateReportCampaignSegments: (
    credentialId: string,
    adAccountId: string,
    body: {
      segment: ReportSegment | null;
      campaignKeys?: ReportCampaignKey[];
      match?: ReportCampaignMatch;
    }
  ): Promise<ApiResponse<{ updated: number; segment: ReportSegment | null }>> =>
    api
      .put(
        `/api/v1/flipkart-ads/connections/${encodeURIComponent(credentialId)}/accounts/${encodeURIComponent(adAccountId)}/reporting/campaign-segments`,
        body
      )
      .then((res) => res.data),
};

export type ConnectionAccount = {
  marketplace: string;
  externalAccountId: string;
  profileId: string;
  accountName?: string | null;
  accountType?: string | null;
  marketplaceId?: string | null;
  countryCode?: string | null;
  managerAccountId?: string | null;
  managerAccountName?: string | null;
  status: string;
  isEnabled: boolean;
  alreadySynced: boolean;
  lastError?: string | null;
  lastSyncedAt?: string | null;
  lastSyncStatus?: string | null;
};

export type ConnectionsAccountsData = {
  marketplaces: Array<{
    marketplace: string;
    label: string;
    accounts: ConnectionAccount[];
  }>;
  accounts: ConnectionAccount[];
};

export type AdsVendorOption = {
  credentialId: string;
  displayName?: string | null;
  sellingPartnerId?: string | null;
  marketplaceId?: string | null;
  status?: string | null;
  connectionType?: 'seller' | 'vendor' | string | null;
  marketplaceMatch?: boolean;
};

export type AdsVendorLinkProfile = {
  profileId: string;
  accountName?: string | null;
  marketplaceId?: string | null;
  vendors: AdsVendorOption[];
};

export type AdsVendorLinksData = {
  profiles: AdsVendorLinkProfile[];
  availableVendors: AdsVendorOption[];
};

export type AdsVendorLinkProfilePayload = {
  profile: AdsVendorLinkProfile;
  availableVendors: AdsVendorOption[];
};

export type WorkspaceRetailKpiPeriod = {
  mapped: boolean;
  credentialIds: string[];
  totalSales: number | null;
  organicSales: number | null;
  totalOrders: number | null;
  organicOrders: number | null;
  tacos: number | null;
  glanceViews?: number | null;
};

export type WorkspaceRetailKpisResponse = {
  profileId: string;
  startDate: string;
  endDate: string;
  compareStartDate?: string;
  compareEndDate?: string;
  /** Last complete vendor retail day (lag-2). */
  dataThrough?: string | null;
  current: WorkspaceRetailKpiPeriod;
  previous?: WorkspaceRetailKpiPeriod;
};

export const connectionsApi = {
  listAccounts: (): Promise<ApiResponse<ConnectionsAccountsData>> =>
    api.get('/api/v1/connections/accounts').then((res) => res.data),

  setupAccounts: (body: {
    marketplace: string;
    externalAccountIds: string[];
  }): Promise<
    ApiResponse<{
      results: Array<{
        externalAccountId: string;
        status: string;
        alreadyReady: boolean;
        started: boolean;
        message?: string;
      }>;
    }>
  > => api.post('/api/v1/connections/accounts/setup', body).then((res) => res.data),

  setEnabled: (
    marketplace: string,
    externalAccountId: string,
    isEnabled: boolean
  ): Promise<ApiResponse<{ isEnabled: boolean; status: string }>> =>
    api
      .patch(
        `/api/v1/connections/accounts/${encodeURIComponent(marketplace)}/${encodeURIComponent(externalAccountId)}`,
        { isEnabled }
      )
      .then((res) => res.data),

  listAdsVendorLinks: (): Promise<ApiResponse<AdsVendorLinksData>> =>
    api.get('/api/v1/connections/ads-vendor-links').then((res) => res.data),

  replaceAdsVendorLinks: (
    profileId: string,
    credentialIds: string[]
  ): Promise<ApiResponse<AdsVendorLinkProfilePayload>> =>
    api
      .put(`/api/v1/connections/ads-vendor-links/${encodeURIComponent(profileId)}`, {
        credentialIds,
      })
      .then((res) => res.data),
};

export type BrandAccount = {
  system: string;
  externalId: string;
  label: string;
  currency: string | null;
};

export type BrandRecord = {
  id: string;
  name: string;
  accounts: BrandAccount[];
};

export type LinkableBrandAccount = BrandAccount & {
  brandId: string | null;
  brandName: string | null;
};

export type LinkableBrandGroup = {
  system: string;
  label: string;
  accounts: LinkableBrandAccount[];
};

export const brandsApi = {
  list: (): Promise<ApiResponse<{ brands: BrandRecord[] }>> =>
    api.get('/api/v1/brands').then((res) => res.data),

  get: (brandId: string): Promise<ApiResponse<{ brand: BrandRecord }>> =>
    api.get(`/api/v1/brands/${encodeURIComponent(brandId)}`).then((res) => res.data),

  create: (name: string): Promise<ApiResponse<{ brand: BrandRecord }>> =>
    api.post('/api/v1/brands', { name }).then((res) => res.data),

  rename: (brandId: string, name: string): Promise<ApiResponse<{ brand: BrandRecord }>> =>
    api.patch(`/api/v1/brands/${encodeURIComponent(brandId)}`, { name }).then((res) => res.data),

  remove: (brandId: string): Promise<ApiResponse<{ id: string }>> =>
    api.delete(`/api/v1/brands/${encodeURIComponent(brandId)}`).then((res) => res.data),

  linkableAccounts: (): Promise<ApiResponse<{ groups: LinkableBrandGroup[] }>> =>
    api.get('/api/v1/brands/linkable-accounts').then((res) => res.data),

  replaceAccounts: (
    brandId: string,
    accounts: Array<{ system: string; externalId: string }>
  ): Promise<ApiResponse<{ brand: BrandRecord }>> =>
    api
      .put(`/api/v1/brands/${encodeURIComponent(brandId)}/accounts`, { accounts })
      .then((res) => res.data),

  getAdsReport: (
    brandId: string,
    params: { startDate: string; endDate: string }
  ): Promise<ApiResponse<BrandAdsReportPayload>> =>
    api
      .get(`/api/v1/brands/${encodeURIComponent(brandId)}/ads-report`, {
        params: { startDate: params.startDate, endDate: params.endDate },
        timeout: HEAVY_REQUEST_TIMEOUT_MS,
      })
      .then((res) => res.data),
};

export type SpApiConnectionType = 'seller' | 'vendor';

export type SpApiVendorEnrollment = {
  id: string;
  marketplace: string;
  externalAccountId: string;
  displayName?: string | null;
  accountType?: string | null;
  marketplaceId?: string | null;
  status: string;
  isEnabled: boolean;
  lastError?: string | null;
  setupStartedAt?: string | null;
  readyAt?: string | null;
  alreadySynced?: boolean;
};

export type SpApiOauthConnection = {
  id: string;
  connection_type: SpApiConnectionType;
  auth_source?: 'oauth' | 'self_authorized' | string;
  selling_partner_id: string;
  region: string;
  marketplace_id: string | null;
  display_name: string | null;
  scopes: string | null;
  consent_granted_at: string | null;
  expires_at: string | null;
  last_api_success_at: string | null;
  last_sales_fetched_at: string | null;
  last_asins_fetched_at: string | null;
  status: string;
  health: 'active' | 'expiring_soon' | 'reauth_required' | 'inactive_risk' | string;
  created_at: string | null;
  accounts?: SpApiDiscoveredAccount[];
  account_count?: number;
  enrollment?: SpApiVendorEnrollment | null;
  setup_status?: string;
  is_enabled?: boolean;
  last_error?: string | null;
  created_by_email?: string | null;
};

export type SpApiDiscoveredAccount = {
  id: string;
  credential_id: string;
  organization_id: string | null;
  connection_type: SpApiConnectionType;
  selling_partner_id: string;
  marketplace_id: string | null;
  external_account_key: string;
  store_name: string | null;
  display_name: string | null;
  country_code: string | null;
  currency_code: string | null;
  is_participating: boolean | null;
  created_at: string | null;
  updated_at: string | null;
};

export type SpApiRegionMarketplace = {
  id: string;
  label: string;
};

export type SpApiRegion = {
  code: string;
  label: string;
  marketplaces: SpApiRegionMarketplace[];
};

export type SpApiVendorLocation = {
  code: string;
  label: string;
  region: string;
  consent_base: string;
};

export type SpApiRegionsResponse = {
  regions: SpApiRegion[];
  vendor_locations?: SpApiVendorLocation[];
  default_region?: string;
  default_vendor_location?: string;
};

export type SpApiSalesSummary = {
  ordered_revenue: number;
  ordered_units: number;
  shipped_units: number | null;
  customer_returns: number | null;
  currency: string | null;
  periods?: unknown[];
  asin_count?: number | null;
};

export type SpApiSalesAsinRow = {
  asin: string;
  startDate?: string | null;
  endDate?: string | null;
  orderedUnits?: number | null;
  orderedRevenue?: number | null;
  shippedUnits?: number | null;
  shippedRevenue?: number | null;
  customerReturns?: number | null;
  currency?: string | null;
};

export type SpApiSalesResponse = {
  cached: boolean;
  fetched_at: string | null;
  connection: SpApiOauthConnection;
  report_type: string;
  report_id?: string;
  start?: string;
  end?: string;
  period?: string;
  summary: SpApiSalesSummary;
  sales_by_asin?: SpApiSalesAsinRow[];
  count?: number;
  syncing?: boolean;
};

export type SpApiAsinRow = {
  id?: string;
  asin: string;
  sku?: string | null;
  title?: string | null;
  imageUrl?: string | null;
  status?: string | null;
  sellableOnHandInventoryUnits?: number | null;
  openPurchaseOrderUnits?: number | null;
  unsellableOnHandInventoryUnits?: number | null;
  aged90PlusDaysSellableInventoryUnits?: number | null;
  unhealthyInventoryUnits?: number | null;
  endDate?: string | null;
  marketplace_id?: string | null;
  synced_at?: string | null;
};

export type SpApiAsinsResponse = {
  cached: boolean;
  fetched_at: string | null;
  synced_at?: string | null;
  marketplace_id?: string | null;
  connection: SpApiOauthConnection | {
    id: string;
    connection_type: SpApiConnectionType;
    selling_partner_id: string;
    region: string;
    marketplace_id: string | null;
    display_name: string | null;
  };
  report_type?: string;
  report_id?: string;
  count: number;
  asins: SpApiAsinRow[];
  start?: string;
  end?: string;
  syncing?: boolean;
};

export type SpApiResolveResponse = {
  connection: SpApiOauthConnection | null;
  connections?: SpApiOauthConnection[];
  marketplace_id: string | null;
  mapped?: boolean;
  matched_marketplace?: boolean;
  message?: string;
};

export type SpApiTrafficRow = {
  asin: string;
  glanceViews: number;
  startDate?: string | null;
  endDate?: string | null;
};

export type SpApiTrafficResponse = {
  connection: SpApiOauthConnection;
  report_type: string;
  start?: string;
  end?: string;
  period?: string;
  total_glance_views: number;
  count: number;
  rows: SpApiTrafficRow[];
  syncing?: boolean;
};

export type SpApiOrderRow = {
  purchaseOrderNumber?: string | null;
  purchaseOrderState?: string | null;
  purchaseOrderDate?: string | null;
  asin?: string | null;
  vendorProductIdentifier?: string | null;
  orderedQuantity?: number | null;
  acceptedQuantity?: number | null;
  receivedQuantity?: number | null;
  title?: string | null;
};

export type SpApiOrdersResponse = {
  connection: SpApiOauthConnection;
  source: string;
  created_after?: string;
  created_before?: string;
  count: number;
  rows: SpApiOrderRow[];
};

export type SpApiBrandAnalyticsCatalogRow = {
  asin: string;
  startDate?: string | null;
  endDate?: string | null;
  impressionCount?: number;
  clickCount?: number;
  clickRate?: number | null;
  cartAddCount?: number;
  purchaseCount?: number;
  searchTrafficSales?: number | null;
  conversionRate?: number | null;
  currency?: string | null;
};

export type SpApiBrandAnalyticsSearchTermRow = {
  departmentName?: string | null;
  searchTerm?: string | null;
  searchFrequencyRank?: number | null;
  clickedAsin?: string | null;
  clickShareRank?: number | null;
  clickShare?: number | null;
  conversionShare?: number | null;
};

export type SpApiRepeatPurchaseRow = {
  asin: string;
  startDate?: string | null;
  endDate?: string | null;
  orders?: number | null;
  uniqueCustomers?: number | null;
  repeatCustomersPctTotal?: number | null;
  repeatPurchaseRevenue?: number | null;
  repeatPurchaseRevenuePctTotal?: number | null;
  currency?: string | null;
};

export type SpApiMarketBasketRow = {
  asin: string;
  purchasedWithAsin?: string | null;
  purchasedWithRank?: number | null;
  combinationPct?: number | null;
  startDate?: string | null;
  endDate?: string | null;
};

export type SpApiInventoryRow = {
  asin: string;
  startDate?: string | null;
  endDate?: string | null;
  sellableOnHandInventoryUnits?: number | null;
  unsellableOnHandInventoryUnits?: number | null;
  aged90PlusDaysSellableInventoryUnits?: number | null;
  unhealthyInventoryUnits?: number | null;
  openPurchaseOrderUnits?: number | null;
};

export type SpApiInventoryResponse = {
  connection: SpApiOauthConnection;
  report_type: string;
  start?: string;
  end?: string;
  period?: string;
  count: number;
  rows: SpApiInventoryRow[];
  cached?: boolean;
  syncing?: boolean;
};

export type SpApiBrandAnalyticsResponse = {
  connection: SpApiOauthConnection;
  catalog_performance: {
    report_type: string;
    start?: string;
    end?: string;
    period?: string;
    count: number;
    rows: SpApiBrandAnalyticsCatalogRow[];
  };
  search_terms: {
    report_type: string;
    count: number;
    rows: SpApiBrandAnalyticsSearchTermRow[];
  } | null;
  search_terms_error?: unknown;
  repeat_purchase?: {
    rows: SpApiRepeatPurchaseRow[];
    brand?: Record<string, unknown> | null;
  };
  market_basket?: SpApiMarketBasketRow[];
  syncing?: boolean;
};

export type SpApiGeoSelloutRow = {
  asin: string;
  shipToCountryCode?: string | null;
  shipToStateOrProvince?: string | null;
  orderedUnits?: number | null;
  orderedRevenue?: number | null;
  shippedUnits?: number | null;
  shippedRevenue?: number | null;
  currency?: string | null;
};

export type SpApiGeoSelloutResponse = {
  connection: SpApiOauthConnection;
  source: string;
  start?: string;
  end?: string;
  count: number;
  rows: SpApiGeoSelloutRow[];
};

export type SpApiReviewRow = {
  asin: string;
  itemName?: string | null;
  sentiment?: string | null;
  topic?: string | null;
  mentionCount?: number | null;
  starRatingImpact?: number | null;
  error?: string | null;
};

export type SpApiReviewsResponse = {
  connection: SpApiOauthConnection;
  source: string;
  note?: string;
  asin_count: number;
  count: number;
  rows: SpApiReviewRow[];
  syncing?: boolean;
};

export type SpApiOrganicPaidRow = {
  asin: string;
  totalOrderedUnits?: number | null;
  totalOrderedRevenue?: number | null;
  organicUnits?: number | null;
  organicRevenue?: number | null;
  paidUnits?: number | null;
  paidRevenue?: number | null;
  currency?: string | null;
  startDate?: string | null;
  endDate?: string | null;
};

export type SpApiOrganicPaidResponse = {
  connection: SpApiOauthConnection;
  source: string;
  note?: string;
  start?: string;
  end?: string;
  count: number;
  rows: SpApiOrganicPaidRow[];
  brand_analytics_error?: unknown;
};

export const spApiOauthApi = {
  listRegions: (): Promise<ApiResponse<SpApiRegionsResponse>> =>
    api.get('/api/v1/sp-api/meta/regions').then((res) => res.data),

  getAuthorizeUrl: (params: {
    type: SpApiConnectionType;
    region?: string;
    location?: string;
    credentialId?: string | null;
  }): Promise<
    ApiResponse<{
      authorize_url: string;
      state: string;
      redirect_uri: string;
      connection_type: SpApiConnectionType;
      region: string;
      location?: string;
    }>
  > =>
    api
      .get('/api/v1/sp-api/oauth/authorize', {
        params: {
          type: params.type,
          ...(params.region ? { region: params.region } : {}),
          ...(params.location ? { location: params.location } : {}),
          ...(params.credentialId ? { credential_id: params.credentialId } : {}),
        },
      })
      .then((res) => res.data),

  listConnections: (params?: {
    type?: SpApiConnectionType;
  }): Promise<ApiResponse<{ connections: SpApiOauthConnection[] }>> =>
    api
      .get('/api/v1/sp-api/oauth/connections', {
        params: params?.type ? { type: params.type } : undefined,
      })
      .then((res) => res.data),

  listAccounts: (params?: {
    type?: SpApiConnectionType;
  }): Promise<ApiResponse<{ accounts: SpApiDiscoveredAccount[] }>> =>
    api
      .get('/api/v1/sp-api/oauth/accounts', {
        params: params?.type ? { type: params.type } : undefined,
      })
      .then((res) => res.data),

  refreshAccounts: (
    credentialId: string
  ): Promise<
    ApiResponse<{ connection: SpApiOauthConnection; accounts: SpApiDiscoveredAccount[] }>
  > =>
    api
      .post(
        `/api/v1/sp-api/oauth/connections/${encodeURIComponent(credentialId)}/refresh-accounts`
      )
      .then((res) => res.data),

  resolveConnection: (params?: {
    marketplaceId?: string | null;
    profileId?: string | null;
  }): Promise<ApiResponse<SpApiResolveResponse>> =>
    api
      .get('/api/v1/sp-api/connections/resolve', {
        params: {
          ...(params?.marketplaceId ? { marketplace_id: params.marketplaceId } : {}),
          ...(params?.profileId ? { profile_id: params.profileId } : {}),
        },
      })
      .then((res) => res.data),

  reconnect: (
    credentialId: string
  ): Promise<
    ApiResponse<{
      authorize_url: string;
      state: string;
      redirect_uri: string;
      connection_type: SpApiConnectionType;
      region: string;
    }>
  > =>
    api
      .post(`/api/v1/sp-api/oauth/connections/${encodeURIComponent(credentialId)}/reconnect`)
      .then((res) => res.data),

  selfAuthorize: (body: {
    refresh_token: string;
    region: string;
    marketplace_id: string;
    selling_partner_id?: string | null;
    display_name?: string | null;
    credential_id?: string | null;
  }): Promise<ApiResponse<SpApiOauthConnection>> =>
    api.post('/api/v1/sp-api/connections/self-authorize', body).then((res) => res.data),

  getSales: (
    credentialId: string,
    params?: {
      start?: string;
      end?: string;
      period?: string;
      view?: string;
      program?: string;
      refresh?: boolean;
    }
  ): Promise<ApiResponse<SpApiSalesResponse>> =>
    api
      .get(`/api/v1/sp-api/connections/${encodeURIComponent(credentialId)}/sales`, { params })
      .then((res) => res.data),

  getAsins: (
    credentialId: string,
    params?: {
      marketplace_id?: string;
      start?: string;
      end?: string;
      period?: string;
      view?: string;
      program?: string;
      refresh?: boolean;
    }
  ): Promise<ApiResponse<SpApiAsinsResponse>> =>
    api
      .get(`/api/v1/sp-api/connections/${encodeURIComponent(credentialId)}/asins`, { params })
      .then((res) => res.data),

  getTraffic: (
    credentialId: string,
    params?: { start?: string; end?: string; period?: string }
  ): Promise<ApiResponse<SpApiTrafficResponse>> =>
    api
      .get(`/api/v1/sp-api/connections/${encodeURIComponent(credentialId)}/traffic`, { params })
      .then((res) => res.data),

  getInventory: (
    credentialId: string,
    params?: { start?: string; end?: string; period?: string }
  ): Promise<ApiResponse<SpApiInventoryResponse>> =>
    api
      .get(`/api/v1/sp-api/connections/${encodeURIComponent(credentialId)}/inventory`, { params })
      .then((res) => res.data),

  getOrders: (
    credentialId: string,
    params?: { start?: string; end?: string; limit?: number }
  ): Promise<ApiResponse<SpApiOrdersResponse>> =>
    api
      .get(`/api/v1/sp-api/connections/${encodeURIComponent(credentialId)}/orders`, { params })
      .then((res) => res.data),

  getBrandAnalytics: (
    credentialId: string,
    params?: { start?: string; end?: string; period?: string; include_search_terms?: boolean }
  ): Promise<ApiResponse<SpApiBrandAnalyticsResponse>> =>
    api
      .get(`/api/v1/sp-api/connections/${encodeURIComponent(credentialId)}/brand-analytics`, {
        params,
      })
      .then((res) => res.data),

  getGeoSellout: (
    credentialId: string,
    params?: { start?: string; end?: string; period?: string }
  ): Promise<ApiResponse<SpApiGeoSelloutResponse>> =>
    api
      .get(`/api/v1/sp-api/connections/${encodeURIComponent(credentialId)}/geo-sellout`, { params })
      .then((res) => res.data),

  getReviews: (
    credentialId: string,
    params?: { asins?: string; max_asins?: number }
  ): Promise<ApiResponse<SpApiReviewsResponse>> =>
    api
      .get(`/api/v1/sp-api/connections/${encodeURIComponent(credentialId)}/reviews`, { params })
      .then((res) => res.data),

  getOrganicPaid: (
    credentialId: string,
    params?: { start?: string; end?: string; period?: string; view?: string; program?: string }
  ): Promise<ApiResponse<SpApiOrganicPaidResponse>> =>
    api
      .get(`/api/v1/sp-api/connections/${encodeURIComponent(credentialId)}/organic-paid`, {
        params,
      })
      .then((res) => res.data),
};
